import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageCircle, CheckCircle2, XCircle, Clock, Star, Wallet, ShieldCheck } from "lucide-react";
import { useAuth } from '../context/AuthContext';
import { useLoginModal } from '../components/LoginModalProvider';
import { supabase } from '../lib/supabase';
import AppLogo from '../components/AppLogo';
import { useBreakpoint } from '../hooks/useBreakpoint';

/* ─── Font injection ─── */
if (!document.getElementById("__settings_fonts")) {
  const l = document.createElement("link");
  l.id = "__settings_fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap";
  document.head.appendChild(l);
}

/* ─── Design tokens — identical set to ProDashboardHome.jsx so the
   customer side and the pro side are unmistakably the same product. ─── */
const T = {
  green:       "#1e9254",
  greenDark:   "#155c36",
  greenDeep:   "#0d2e1a",
  greenTint:   "#eef8f3",
  greenBd:     "#a7d9bc",

  bg:          "#f2f6f3",
  white:       "#ffffff",
  card2:       "#f7f8fa",
  border:      "#e4e7ef",
  borderFaint: "#eef0f4",
  hoverBg:     "#F3F4F6",

  text:        "#0f1117",
  textMid:     "#2d3549",
  sub:         "#4a5268",
  faint:       "#8b93a7",
  ghost:       "#c7cddb",

  amber:       "#92400e",
  amberTint:   "#fefaf1",
  amberBd:     "#f3d99a",

  red:         "#b3261e",
  redTint:     "#fdf3f2",
  redBd:       "#f0c9c5",

  blue:        "#1d4ed8",
  blueTint:    "#eef3fd",
  blueBd:      "#c7d7f7",

  gold:        "#b8862f",
  goldTint:    "#fbf5e9",
  goldBd:      "#e9d5a3",

  mpesa:       "#4CAF50",
  mpesaDark:   "#0a5c2e",

  serif: "'Instrument Serif', Georgia, serif",
  sans:  "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",

  r8:"8px", r10:"10px", r12:"12px", r14:"14px", r16:"16px", r20:"20px", rFull:"9999px",

  s1: "0 1px 3px rgba(13,46,26,0.05)",
  s2: "0 4px 16px rgba(13,46,26,0.07)",
  s3: "0 10px 34px rgba(13,46,26,0.14)",

  tx: "all 0.18s cubic-bezier(.4,0,.2,1)",
};

/* ─── Global styles ─── */
if (!document.getElementById("settings-global-styles")) {
  const style = document.createElement("style");
  style.id = "settings-global-styles";
  style.textContent = `
    *, *::before, *::after { box-sizing: border-box; }
    @keyframes fadeO { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideU { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes csPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
    @keyframes csPopIn { from { opacity:0; transform: translateY(-6px) scale(0.98); } to { opacity:1; transform: translateY(0) scale(1); } }
    button:focus-visible, a:focus-visible, input:focus-visible { outline: 2px solid ${T.green}; outline-offset: 2px; }
    input:focus { outline: none; }
    input::placeholder { color: ${T.ghost}; }
    ::-webkit-scrollbar { width:5px; height:5px; }
    ::-webkit-scrollbar-track { background:transparent; }
    ::-webkit-scrollbar-thumb { background:#D7DBE2; border-radius:99px; }
    .cs-scroll::-webkit-scrollbar { display:none; }
    .cs-scroll { scrollbar-width:none; -ms-overflow-style:none; }
    .addr-card:hover { border-color: ${T.greenBd} !important; background: ${T.greenTint} !important; }
    .cs-nav-item:hover { background: ${T.white} !important; }
    .cs-dropdown-item:hover { background: ${T.card2} !important; }
    .remove-photo:hover { text-decoration: underline; color: ${T.sub} !important; }
    .trust-card:hover { border-color: ${T.greenBd} !important; box-shadow: 0 8px 32px rgba(30,146,84,0.12) !important; }
    .quick-link-row:hover span { color: ${T.green} !important; }
    .quick-link-row:hover svg { stroke: ${T.green} !important; }
    .cs-foot-link:hover { color: #fff !important; }
    .cs-accordion-row:hover { background: ${T.card2}; }
    .pd-icon-badge:hover { transform: translateY(-1px); box-shadow: ${T.s2}; }
    @media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation:none !important; transition:none !important; } }
  `;
  document.head.appendChild(style);
}

/* ─── Icon set ─── */
function Icon({ name, size = 20, stroke = "currentColor", strokeWidth = 1.5 }) {
  const icons = {
    user: <><circle cx="12" cy="8" r="4"/><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/></>,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    bell: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>,
    wallet: <><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 0 0 0 4h4v-4Z"/></>,
    mapPin: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></>,
    home: <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M9 22V12h6v10"/></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    edit: <><path d="M17 3l4 4-11 11H6v-4L17 3z"/></>,
    trash: <><path d="M3 6h18"/><path d="M8 6v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6"/><path d="M10 11v4"/><path d="M14 11v4"/></>,
    plus: <><path d="M12 5v14M5 12h14"/></>,
    check: <><path d="M20 6L9 17l-5-5"/></>,
    x: <><path d="M18 6L6 18M6 6l12 12"/></>,
    info: <><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></>,
    logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></>,
    chevronRight: <><polyline points="9 18 15 12 9 6"/></>,
    chevronDown: <><polyline points="6 9 12 15 18 9"/></>,
    star: <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></>,
    search: <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,
    arrowRight: <><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></>,
    zap: <><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
    messageSquare: <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>,
    calendarDays: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
    heart: <><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></>,
    refresh: <><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></>,
    chevronLeft: <><polyline points="15 18 9 12 15 6"/></>,
    ticket: <><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z"/><line x1="13" y1="5" x2="13" y2="19" strokeDasharray="2 2"/></>,
    gift: <><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/><path d="M19 12v7a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7"/><path d="M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/></>,
    settings: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>,
    users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
    globe: <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      {icons[name]}
    </svg>
  );
}

/* ─── Small shared primitives (same visual language as the pro dashboard) ─── */
function Badge({ children, variant = "neutral", size = "md" }) {
  const map = {
    neutral: { bg: T.card2,     color: T.sub,   bd: T.border  },
    green:   { bg: T.greenTint, color: T.green, bd: T.greenBd },
    amber:   { bg: T.amberTint, color: T.amber, bd: T.amberBd },
    blue:    { bg: T.blueTint,  color: T.blue,  bd: T.blueBd  },
    red:     { bg: T.redTint,   color: T.red,   bd: T.redBd   },
    gold:    { bg: T.goldTint,  color: T.gold,  bd: T.goldBd  },
  };
  const s = map[variant] || map.neutral;
  const pad = size === "sm" ? "2px 8px" : "3px 10px";
  const fs  = size === "sm" ? 10.5 : 11.5;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: s.bg, color: s.color, border: `1px solid ${s.bd}`, borderRadius: T.rFull, padding: pad, fontSize: fs, fontWeight: 700, fontFamily: T.sans, whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function Btn({ children, v = "ghost", size = "md", onClick, disabled = false, full = false, icon, type = "button" }) {
  const [hov, setHov] = useState(false);
  const sz = { sm: { fs: 12.5, h: 34, pad: "0 16px" }, md: { fs: 13, h: 40, pad: "0 20px" } }[size];
  const vs = {
    primary: { bg: hov ? T.greenDark : T.green, col: "#fff", bd: "1px solid transparent", sh: hov ? "0 6px 20px rgba(30,146,84,0.24)" : "0 2px 10px rgba(30,146,84,0.2)" },
    ghost:   { bg: hov ? T.hoverBg : T.white,    col: T.textMid, bd: `1.5px solid ${T.border}`, sh: "none" },
    outline: { bg: hov ? T.greenTint : T.white,  col: T.green,   bd: `1.5px solid ${T.greenBd}`, sh: "none" },
  };
  const s = vs[v] || vs.ghost;
  return (
    <button type={type} onClick={onClick} disabled={disabled} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7, height: sz.h, padding: sz.pad, borderRadius: T.rFull, fontSize: sz.fs, fontWeight: 700, fontFamily: T.sans, background: s.bg, color: s.col, border: s.bd, boxShadow: s.sh, cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.45 : 1, whiteSpace: "nowrap", outline: "none", minHeight: 44, transition: T.tx, ...(full ? { width: "100%" } : {}) }}>
      {icon}{children}
    </button>
  );
}

/* ─── M-PESA mark — the real brand mark (supplied SVG), set on a white
   chip like a card-network logo (Visa/Mastercard style badge) since the
   logo's own colours need a light background to read correctly. ─── */
function MpesaLogoGlyph({ h }) {
  const w = h * (512 / 273);
  return (
    <svg viewBox="0 0 512 273" width={w} height={h} style={{ display: "block" }}>
      <path d="m361 184c-6.68-1.88-14.8-5.54-19.9-8.94-1.52-1.02-1.18-2.66 1.73-8.42l3.62-7.15 9.09 4.74c15.9 8.33 29 7.71 29-1.4 0-4.39-3.64-6.71-16.4-10.4-17.5-5.12-25.4-13.2-24.1-24.4 1.71-14 12.7-21.4 31.2-21.3 10.9 0.114 26.2 4.19 28.2 7.54 0.754 1.22-4.68 15.5-5.89 15.5-0.323 0-2.95-1.18-5.82-2.62-13.7-6.88-27.1-6.75-27.1 0.259 0 4.67 3.4 7.15 14.8 10.8 14.8 4.79 20 8.11 23.4 15.1 3.36 6.94 2.86 14-1.48 21.2-5.65 9.29-25.2 13.9-40.6 9.62zm-351-38.1v-38.8h21.2l11.3 22.6c6.22 12.4 11.6 22.6 12 22.6s5.77-10.2 12-22.6l11.3-22.6h21.2v77.6h-16.9v-24c0-13.2-0.486-24-1.08-24-0.595 0-5.3 8.88-10.5 19.7l-9.38 19.7h-13.1l-20.3-41.6-0.77 50.1h-16.9zm188-0.203v-38.9l21.7 0.73c18.6 0.626 22.7 1.18 28.2 3.85 9.45 4.57 12.7 9.93 13.3 21.9 0.942 19.9-8.5 28.6-32.1 30l-12.7 0.725v20.7h-18.3zm41.7-0.948c3.02-2.59 3.79-4.5 3.79-9.29 0-9.26-3.54-11.9-16.6-12.6l-10.6-0.533v25.7h9.79c8.21 0 10.4-0.53 13.6-3.27zm31.6 1.15v-38.8h59.2v15.5h-40.9v15.5h38.1v15.5h-38.1v15.5h42.3v15.5h-60.6zm142 30.7c1.93-4.5 9.38-21.9 16.5-38.5l13-30.3 19.7-0.805 15.1 36.4c8.32 20 15.5 37.5 16.1 38.9 0.83 2.31 0.0141 2.5-9.31 2.12l-10.2-0.417-4.68-12.7h-34.8l-4.95 12.7-20 0.828zm49.6-21.9c-6.36-17.3-10.1-26.4-10.8-26.4-0.478 7e-3 -3.09 5.84-5.81 13-2.71 7.12-5.15 13.5-5.4 14.1-0.258 0.625 4.72 1.14 11.1 1.14 8.64 0 11.4-0.443 10.9-1.76z" fill="#39b54a" stroke="#39b54a" strokeWidth="2"/>
      <path d="m172 80.6v9.36c-15.2 0.0524-30.2 0.0158-45.3 0.0158-12.9 0-14.4 6.09-14.4 15.7v131c0 7.81 6.44 14.1 14.4 14.1h43.1c7.99 0 14.4-6.29 14.4-14.1v-157c0-1.97-1.59-5.95-5.39-5.77-4.27 0.237-6.78 3.37-6.91 5.77zm-37.1 27.1h27.6c11.6 0 13.2 7.25 13.2 15.1v46.7c0 5.8-5.03 14.9-13.2 14.9h-27.6c-9.97 0-15.2-8.6-15.2-14.9v-46.7c0-7.81 4.13-15.1 15.2-15.1z" fill="#d8e3d2" fillRule="evenodd" strokeWidth=".998"/>
      <path d="m104 155c9.07-0.942 17.2-5.6 26.4-17.6 10.4 15.8 24.7 15.2 39.2 16.8-15.6 0.163-13.6 5.52-34.8 4.03-6.77-0.473-17.9-0.936-30.7-3.19z" fill="#9d4c44" fillRule="evenodd" strokeWidth=".998"/>
      <path d="m161 122 33.8 16.4c-24.1 26.9-58.5 25.1-90.2 16.8 17.5-0.0728 33 6.4 56.3-33.1z" fill="#ed1c24" fillRule="evenodd" strokeWidth=".998"/>
    </svg>
  );
}
function MpesaMark({ size = "md" }) {
  const h = size === "sm" ? 17 : 21;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      height: h + 8, padding: "0 6px", borderRadius: 5,
      background: T.white, border: `1px solid ${T.border}`,
      boxShadow: T.s1, flexShrink: 0,
    }}>
      <MpesaLogoGlyph h={h} />
    </span>
  );
}

/* ─── ToastStack — elevated white cards, matches the pro dashboard's
   toast treatment instead of a dark pill. ─── */
function ToastStack({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div style={{ position: "fixed", top: 16, right: 16, zIndex: 2000, display: "flex", flexDirection: "column", gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} onClick={() => onDismiss(t.id)} style={{
          background: T.white, border: `1px solid ${t.type === "error" ? T.redBd : T.greenBd}`,
          boxShadow: T.s3, borderRadius: T.r12, padding: "12px 16px", display: "flex", alignItems: "center", gap: 10,
          minWidth: 260, maxWidth: 340, cursor: "pointer", animation: "csPopIn 0.2s ease-out",
        }}>
          {t.type === "error" ? <Icon name="x" size={16} stroke={T.red} strokeWidth={2} /> : <Icon name="check" size={16} stroke={T.green} strokeWidth={2.2} />}
          <span style={{ fontSize: 13, color: T.textMid, fontFamily: T.sans, lineHeight: 1.4 }}>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── time-ago formatter used by both inline dropdowns below ─── */
function timeAgo(ts) {
  if (!ts) return "";
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1)   return "now";
  if (mins < 60)  return `${mins}m`;
  if (hrs < 24)   return `${hrs}h`;
  if (days === 1) return "yesterday";
  return new Date(ts).toLocaleDateString("en-KE", { month: "short", day: "numeric" });
}
function getInitials(name) {
  if (!name) return "?";
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}
/* Avatar tint pool — same pairing as <Badge>, deterministic by name. */
const AVATAR_PAIRS = [
  { bg: T.greenTint, fg: T.greenDark },
  { bg: T.blueTint,  fg: T.blue },
  { bg: T.amberTint, fg: T.amber },
  { bg: T.goldTint,  fg: T.gold },
  { bg: T.redTint,   fg: T.red },
  { bg: T.card2,     fg: T.sub },
];
function avatarColor(name) {
  const idx = (name || "").charCodeAt(0) % AVATAR_PAIRS.length;
  return AVATAR_PAIRS[idx] || AVATAR_PAIRS[0];
}

/* ─── TopBarIconButton — same circular tinted-badge shell as
   ProDashboardHome's message/bell buttons: circle + colored tint
   background + border, a lucide icon (not the page's hand-drawn Icon
   set), and a small red count badge overlapping the top-right corner.
   Sized down on mobile so it sits comfortably next to the smaller
   logo and profile avatar instead of matching the desktop scale. ─── */
function TopBarIconButton({ IconComp, open, badgeCount, onClick, title, tint }) {
  const { isMobile } = useBreakpoint();
  const capped = badgeCount > 9 ? "9+" : badgeCount;
  const btnSize = isMobile ? 27 : 34;
  const iconSize = isMobile ? 12 : 14;
  return (
    <button onClick={onClick} title={title} className="pd-icon-badge" style={{
      width: btnSize, height: btnSize, borderRadius: "50%", background: tint.bg,
      border: `1px solid ${tint.bd}`, display: "flex", alignItems: "center",
      justifyContent: "center", cursor: "pointer", transition: T.tx,
      position: "relative", padding: 0,
    }}>
      <IconComp size={iconSize} color={tint.fg} strokeWidth={2} />
      {badgeCount > 0 && (
        <span style={{
          position: "absolute", top: isMobile ? -2 : -3, right: isMobile ? -2 : -3, minWidth: isMobile ? 13 : 15, height: isMobile ? 13 : 15,
          borderRadius: "50%", background: T.red, color: "#fff", fontSize: isMobile ? 8 : 9,
          fontWeight: 700, fontFamily: T.sans, display: "flex", alignItems: "center",
          justifyContent: "center", border: "1.5px solid #fff", pointerEvents: "none",
        }}>
          {capped}
        </span>
      )}
    </button>
  );
}

/* ─── NotificationsMenu — real data from `notifications`, realtime,
   mark-read, styled entirely with the page's own T tokens + Icon set. ─── */
function NOTIF_ICON_STYLE(type) {
  const map = {
    booking_accepted:  { fg: T.green, IconComp: CheckCircle2 },
    booking_completed: { fg: T.green, IconComp: CheckCircle2 },
    new_message:       { fg: T.blue,  IconComp: MessageCircle },
    new_review:        { fg: T.amber, IconComp: Star },
    payment_received:  { fg: T.green, IconComp: Wallet },
  };
  return map[type] || { fg: T.blue, IconComp: Bell };
}

function NotificationsMenu({ user, navigate }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const channelRef = useRef(null);
  const { isMobile } = useBreakpoint();

  const unreadCount = notifications.filter(n => !n.read).length;

  const fetchNotifs = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.from("notifications").select("*").eq("user_id", user.id).order("created_at", { ascending: false }).limit(20);
      if (error) throw error;
      setNotifs(data || []);
    } catch (err) {
      console.error("NotificationsMenu fetch error:", err);
    } finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { fetchNotifs(); }, [fetchNotifs]);

  useEffect(() => {
    if (!user?.id) return;
    channelRef.current = supabase.channel(`notifs-badge-${user.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => fetchNotifs())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => fetchNotifs())
      .subscribe();
    return () => { if (channelRef.current) supabase.removeChannel(channelRef.current); };
  }, [user?.id, fetchNotifs]);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => {
    const h = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  const markAllRead = async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (!unreadIds.length) return;
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    try { await supabase.from("notifications").update({ read: true }).in("id", unreadIds); } catch (err) { console.error(err); }
  };
  const markRead = async id => {
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    try { await supabase.from("notifications").update({ read: true }).eq("id", id); } catch {}
  };
  const handleClick = n => { markRead(n.id); setOpen(false); navigate(n.link || "/notifications"); };

  if (!user?.id) return null;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <TopBarIconButton IconComp={Bell} tint={{ bg: T.goldTint, bd: T.goldBd, fg: T.gold }} open={open} badgeCount={unreadCount} onClick={() => setOpen(o => !o)} title="Notifications" />

      {open && (
        <div style={{ position: isMobile ? "fixed" : "absolute", top: isMobile ? 64 : "calc(100% + 10px)", left: isMobile ? 16 : "auto", right: isMobile ? 16 : 0, width: isMobile ? "auto" : 340, maxWidth: isMobile ? "none" : 340, background: T.white, border: `1.5px solid ${T.border}`, borderRadius: T.r16, boxShadow: T.s3, zIndex: 400, overflow: "hidden", animation: "csPopIn 0.17s cubic-bezier(.4,0,.2,1) both" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 10px", borderBottom: `1px solid ${T.borderFaint}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: T.text, fontFamily: T.sans }}>Notifications</span>
              {unreadCount > 0 && <Badge variant="green" size="sm">{unreadCount} new</Badge>}
            </div>
            {unreadCount > 0 && (
              <button onClick={markAllRead} style={{ fontSize: 11, color: T.sub, fontWeight: 600, background: "transparent", border: "none", cursor: "pointer", fontFamily: T.sans, padding: 0 }}>Mark all read</button>
            )}
          </div>

          <div className="cs-scroll" style={{ maxHeight: 360, overflowY: "auto" }}>
            {loading && notifications.length === 0 ? (
              [1, 2, 3].map(i => (
                <div key={i} style={{ display: "flex", gap: 10, padding: "11px 16px", alignItems: "center" }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: T.card2, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 10, width: "55%", background: T.card2, borderRadius: 4, marginBottom: 6 }} />
                    <div style={{ height: 9, width: "75%", background: T.card2, borderRadius: 4 }} />
                  </div>
                </div>
              ))
            ) : notifications.length === 0 ? (
              <div style={{ padding: "36px 20px", textAlign: "center" }}>
                <Bell size={28} color={T.ghost} strokeWidth={1.4} />
                <p style={{ margin: "10px 0 0", fontWeight: 600, color: T.sub, fontSize: 14, fontFamily: T.sans }}>No notifications yet</p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.faint, lineHeight: 1.5, fontFamily: T.sans }}>Booking updates and messages will appear here.</p>
              </div>
            ) : (
              notifications.slice(0, 8).map((n, i) => {
                const s = NOTIF_ICON_STYLE(n.type);
                const isLast = i === Math.min(notifications.length, 8) - 1;
                return (
                  <div key={n.id} onClick={() => handleClick(n)} className="cs-dropdown-item" style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 16px", background: !n.read ? T.greenTint : T.white, borderBottom: isLast ? "none" : `1px solid ${T.borderFaint}`, borderLeft: `3px solid ${!n.read ? T.green : "transparent"}`, cursor: "pointer", transition: "background 0.15s ease" }}>
                    <span style={{ width: 26, height: 26, borderRadius: "50%", background: T.card2, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                      <s.IconComp size={13} color={s.fg} strokeWidth={2} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: 12, fontWeight: !n.read ? 700 : 600, color: T.text, fontFamily: T.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.title || "Notification"}</span>
                        <span style={{ fontSize: 10, color: T.faint, whiteSpace: "nowrap", flexShrink: 0, fontFamily: T.sans }}>{timeAgo(n.created_at)}</span>
                      </div>
                      <p style={{ margin: 0, fontSize: 12, color: !n.read ? T.textMid : T.sub, fontFamily: T.sans, lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", fontWeight: !n.read ? 500 : 400 }}>{n.message || n.body || ""}</p>
                    </div>
                    {!n.read && <div style={{ width: 6, height: 6, borderRadius: "50%", background: T.green, marginTop: 4, flexShrink: 0 }} />}
                  </div>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.borderFaint}`, textAlign: "center" }}>
              <button onClick={() => { setOpen(false); navigate("/notifications"); }} style={{ fontSize: 12, fontWeight: 600, color: T.green, background: "transparent", border: "none", cursor: "pointer", fontFamily: T.sans }}>View all notifications →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── MessagesMenu — real data from `conversations` + `chat_messages`,
   realtime, mark-thread-read, same T tokens/Icon set as the rest of
   the page. ─── */
function MessagesMenu({ user, navigate }) {
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const channelRef = useRef(null);
  const { isMobile } = useBreakpoint();

  const fetchThreads = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data: convos, error } = await supabase.from("conversations").select(`
          id, customer_id, pro_id, updated_at,
          chat_messages ( id, content, sender_id, created_at, status )
        `).or(`customer_id.eq.${user.id},pro_id.eq.${user.id}`).order("updated_at", { ascending: false }).limit(20);
      if (error) throw error;

      const otherIds = (convos || []).map(c => c.customer_id === user.id ? c.pro_id : c.customer_id).filter(Boolean);
      const uniqueIds = [...new Set(otherIds)];
      let profileMap = {};
      if (uniqueIds.length) {
        const { data: profiles } = await supabase.from("profiles").select("id, full_name, avatar_url").in("id", uniqueIds);
        (profiles || []).forEach(p => { profileMap[p.id] = p; });
      }

      const mapped = (convos || []).map(c => {
        const otherId = c.customer_id === user.id ? c.pro_id : c.customer_id;
        const otherProf = profileMap[otherId] || {};
        const msgs = (c.chat_messages || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const lastMsg = msgs[0];
        const unreadCount = msgs.filter(m => m.status !== 'read' && m.sender_id !== user.id).length;
        return {
          id: c.id, otherId, otherName: otherProf.full_name || "JiraniPro", avatarUrl: otherProf.avatar_url || null,
          lastMsg: lastMsg?.content || "", lastTs: lastMsg?.created_at || c.updated_at, unread: unreadCount,
        };
      });

      setThreads(mapped.slice(0, 6));
      setTotalUnread(mapped.reduce((acc, t) => acc + t.unread, 0));
    } catch (err) {
      console.error("MessagesMenu fetch error:", err);
    } finally { setLoading(false); }
  }, [user?.id]);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  useEffect(() => {
    if (!user?.id) return;
    channelRef.current = supabase.channel(`messages-badge-${user.id}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "chat_messages" }, () => fetchThreads())
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "chat_messages" }, () => fetchThreads())
      .subscribe();
    return () => { if (channelRef.current) supabase.removeChannel(channelRef.current); };
  }, [user?.id, fetchThreads]);

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => {
    const h = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  const markThreadRead = async conversationId => {
    setThreads(prev => prev.map(t => t.id === conversationId ? { ...t, unread: 0 } : t));
    setTotalUnread(prev => {
      const thread = threads.find(t => t.id === conversationId);
      return Math.max(0, prev - (thread?.unread || 0));
    });
    try { await supabase.from("chat_messages").update({ status: 'read' }).eq("conversation_id", conversationId).neq("sender_id", user.id); } catch {}
  };
  const handleThreadClick = thread => { markThreadRead(thread.id); setOpen(false); navigate(`/messages/${thread.id}`); };

  if (!user?.id) return null;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <TopBarIconButton IconComp={MessageCircle} tint={{ bg: T.blueTint, bd: T.blueBd, fg: T.blue }} open={open} badgeCount={totalUnread} onClick={() => setOpen(o => !o)} title="Messages" />

      {open && (
        <div style={{ position: isMobile ? "fixed" : "absolute", top: isMobile ? 64 : "calc(100% + 10px)", left: isMobile ? 16 : "auto", right: isMobile ? 16 : 0, width: isMobile ? "auto" : 340, maxWidth: isMobile ? "none" : 340, background: T.white, border: `1.5px solid ${T.border}`, borderRadius: T.r16, boxShadow: T.s3, zIndex: 400, overflow: "hidden", animation: "csPopIn 0.17s cubic-bezier(.4,0,.2,1) both" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 10px", borderBottom: `1px solid ${T.borderFaint}` }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: T.text, fontFamily: T.sans }}>
              Messages {totalUnread > 0 && <span style={{ color: T.green }}>({totalUnread})</span>}
            </span>
            <button onClick={() => { setOpen(false); navigate("/messages"); }} style={{ background: "none", border: "none", fontSize: 12, fontWeight: 600, color: T.green, cursor: "pointer", fontFamily: T.sans, padding: 0 }}>See all</button>
          </div>

          <div className="cs-scroll" style={{ maxHeight: 360, overflowY: "auto" }}>
            {loading && threads.length === 0 ? (
              [1, 2, 3].map(i => (
                <div key={i} style={{ display: "flex", gap: 12, padding: "12px 16px", alignItems: "center" }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", background: T.card2, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 11, width: "60%", background: T.card2, borderRadius: 4, marginBottom: 6 }} />
                    <div style={{ height: 10, width: "80%", background: T.card2, borderRadius: 4 }} />
                  </div>
                </div>
              ))
            ) : threads.length === 0 ? (
              <div style={{ padding: "36px 20px", textAlign: "center" }}>
                <MessageCircle size={28} color={T.ghost} strokeWidth={1.4} />
                <p style={{ margin: "10px 0 0", fontWeight: 600, color: T.sub, fontSize: 14, fontFamily: T.sans }}>No messages yet</p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: T.faint, lineHeight: 1.5, fontFamily: T.sans }}>Messages from your pros will appear here.</p>
              </div>
            ) : (
              threads.map((thread, i) => {
                const { bg, fg } = avatarColor(thread.otherName);
                const isLast = i === threads.length - 1;
                return (
                  <div key={thread.id} onClick={() => handleThreadClick(thread)} className="cs-dropdown-item" style={{ display: "flex", gap: 12, padding: "12px 16px", alignItems: "flex-start", cursor: "pointer", background: thread.unread > 0 ? T.greenTint : T.white, borderBottom: isLast ? "none" : `1px solid ${T.borderFaint}`, borderLeft: `3px solid ${thread.unread > 0 ? T.green : "transparent"}` }}>
                    <div style={{ width: 38, height: 38, borderRadius: "50%", flexShrink: 0, background: bg, color: fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, fontFamily: T.sans, border: `1.5px solid ${T.white}`, boxShadow: T.s1, overflow: "hidden", position: "relative" }}>
                      {thread.avatarUrl ? <img src={thread.avatarUrl} alt={thread.otherName} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : getInitials(thread.otherName)}
                      {thread.unread > 0 && <span style={{ position: "absolute", top: -1, right: -1, width: 10, height: 10, borderRadius: "50%", background: T.green, border: `2px solid ${T.white}` }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 3 }}>
                        <span style={{ fontSize: 13, fontWeight: thread.unread > 0 ? 700 : 500, color: T.text, fontFamily: T.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{thread.otherName}</span>
                        <span style={{ fontSize: 11, color: T.faint, flexShrink: 0, fontFamily: T.sans }}>{timeAgo(thread.lastTs)}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                        <p style={{ margin: 0, fontSize: 12, color: thread.unread > 0 ? T.textMid : T.sub, fontFamily: T.sans, lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: thread.unread > 0 ? 500 : 400, maxWidth: 220 }}>{thread.lastMsg || "No messages yet"}</p>
                        {thread.unread > 0 && (
                          <span style={{ flexShrink: 0, minWidth: 18, height: 18, borderRadius: T.rFull, background: T.green, color: "#fff", fontSize: 10, fontWeight: 700, fontFamily: T.sans, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px" }}>{thread.unread > 9 ? "9+" : thread.unread}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {threads.length > 0 && (
            <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.borderFaint}` }}>
              <Btn v="primary" full onClick={() => { setOpen(false); navigate("/messages"); }}>Open full inbox</Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Profile menu — avatar dropdown, expanded with a couple more
   customer-relevant destinations. Avatar shrinks on mobile so it sits
   at the same visual scale as the mobile logo and message/bell icons
   instead of matching the desktop size. ─── */
function ProfileMenu({ profile, initials, navigate, onSignOut, onJumpToSection }) {
  const [open, setOpen] = useState(false);
  const [hovIdx, setHovIdx] = useState(null);
  const wrapRef = useRef(null);
  const { isMobile } = useBreakpoint();
  const avatarSize = isMobile ? 26 : 32;

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Kept short on purpose — anything already reachable from the sidebar
  // nav or the page's own sections (Addresses, Payment, Notifications,
  // Profile) doesn't need a second entry point here.
  const items = [
    { label: "My Bookings",       icon: "calendarDays",  action: () => navigate("/bookings") },
    { label: "Vouchers & Offers", icon: "ticket",         action: () => navigate("/vouchers") },
    { label: "Favourite Pros",    icon: "heart",          action: () => navigate("/favourites") },
    { label: "Help & Support",    icon: "info",           action: () => navigate("/help") },
  ];

  return (
    <div ref={wrapRef} style={{ position: "relative", flexShrink: 0 }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: "flex", alignItems: "center", gap: isMobile ? 4 : 6, background: "none", border: "none", cursor: "pointer", padding: 2, borderRadius: 999, boxShadow: `0 0 0 2px ${open ? T.green : T.greenBd}`, transition: T.tx }}>
        <div style={{ position: "relative" }}>
          <div style={{ width: avatarSize, height: avatarSize, borderRadius: "50%", background: `linear-gradient(145deg, ${T.green}22, ${T.greenTint})`, border: `2px solid ${T.white}`, boxShadow: "0 1px 4px rgba(30,146,84,0.18)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {profile?.avatar_url
              ? <img src={profile.avatar_url} alt="Avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              : <span style={{ fontSize: isMobile ? 10 : 12.5, fontWeight: 700, color: T.greenDark, fontFamily: T.sans }}>{initials}</span>}
          </div>
          <span style={{ position: "absolute", bottom: -1, right: -1, width: isMobile ? 7 : 9, height: isMobile ? 7 : 9, borderRadius: "50%", background: T.green, border: "2px solid #fff" }} />
        </div>
        <Icon name="chevronDown" size={isMobile ? 11 : 14} stroke={T.faint} strokeWidth={2.2} />
      </button>

      {open && (
        <div style={{ position: "absolute", right: 0, top: "calc(100% + 10px)", width: 252, background: T.white, borderRadius: T.r16, border: `1.5px solid ${T.border}`, boxShadow: T.s3, zIndex: 400, overflow: "hidden" }}>
          <div style={{ padding: "14px 16px 12px", borderBottom: `1px solid ${T.borderFaint}` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{profile?.full_name || "Your account"}</div>
            <div style={{ fontSize: 11, color: T.faint, fontFamily: T.sans, marginTop: 1 }}>{profile?.email || ""}</div>
          </div>
          <div style={{ padding: "6px 0", maxHeight: 360, overflowY: "auto" }} className="cs-scroll">
            {items.map((item, i) => (
              <button key={i} onMouseEnter={() => setHovIdx(i)} onMouseLeave={() => setHovIdx(null)} onClick={() => { item.action(); setOpen(false); }}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", background: hovIdx === i ? T.card2 : "transparent", border: "none", cursor: "pointer", textAlign: "left", fontFamily: T.sans, fontSize: 13, color: T.textMid, transition: "background 0.12s" }}>
                <Icon name={item.icon} size={14} stroke={T.faint} strokeWidth={1.75} /> {item.label}
              </button>
            ))}
          </div>
          <div style={{ borderTop: `1px solid ${T.borderFaint}` }}>
            <button onMouseEnter={() => setHovIdx(99)} onMouseLeave={() => setHovIdx(null)} onClick={() => { onSignOut(); setOpen(false); }}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", background: hovIdx === 99 ? T.redTint : "transparent", border: "none", cursor: "pointer", fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.red, transition: "background 0.12s" }}>
              <Icon name="logout" size={14} stroke={T.red} strokeWidth={1.75} /> Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Trust / Editorial cards (right panel) ─── */
const TRUST_CARDS = [
  { img: "/images/jirani-guarantee.png", eyebrow: "Your protection", heading: "The JiraniApp Guarantee", desc: "Every booking is protected. If something goes wrong, we make it right — no runaround.", link: "/guarantee", linkLabel: "See how we protect you" },
  { img: "/images/verified-pros.png", eyebrow: "Who you're booking", heading: "Verified JiraniPros", desc: "Every pro is identity-checked and reviewed by real clients just like you.", link: "/find-a-jiranipro", linkLabel: "Browse verified pros" },
  { img: "/images/mpesa-safe.png", eyebrow: "Safe payments", heading: "Pay securely with M-Pesa", desc: "Money is held safely and only released after the job is done to your satisfaction.", link: "/how-it-works", linkLabel: "How payments work" },
];

function TrustCard({ card, navigate }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div className="trust-card" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onClick={() => navigate(card.link)}
      style={{ borderRadius: T.r16, overflow: "hidden", border: `1.5px solid ${hovered ? T.greenBd : T.border}`, background: T.white, cursor: "pointer", transition: T.tx, boxShadow: hovered ? "0 8px 28px rgba(30,146,84,0.10)" : T.s1 }}>
      <div style={{ position: "relative", width: "100%", height: 118, overflow: "hidden", background: "#D6EDE2" }}>
        <img src={card.img} alt={card.heading} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 0.45s cubic-bezier(.4,0,.2,1)", transform: hovered ? "scale(1.05)" : "scale(1)" }} onError={e => { e.currentTarget.style.display = "none"; }} />
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 52, background: "linear-gradient(to top, rgba(255,255,255,0.97) 0%, transparent 100%)", pointerEvents: "none" }} />
      </div>
      <div style={{ padding: "12px 16px 16px" }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.green, fontFamily: T.sans, margin: "0 0 4px" }}>{card.eyebrow}</p>
        <p style={{ fontFamily: T.serif, fontSize: 15, fontWeight: 400, color: T.text, lineHeight: 1.3, letterSpacing: "-0.02em", margin: "0 0 6px", fontStyle: "italic" }}>{card.heading}</p>
        <p style={{ fontSize: 12, color: T.sub, lineHeight: 1.6, margin: "0 0 10px", fontFamily: T.sans }}>{card.desc}</p>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: T.green, fontFamily: T.sans, transition: T.tx, transform: hovered ? "translateX(2px)" : "none" }}>
          {card.linkLabel}
          <Icon name="arrowRight" size={11} strokeWidth={2.2} />
        </div>
      </div>
    </div>
  );
}

const CLIENT_LINKS = [
  { label: "How JiraniApp Works", path: "/how-it-works" },
  { label: "Browse by Location",  path: "/browse-by-location" },
  { label: "Customer Reviews",    path: "/customer-reviews" },
  { label: "JiraniApp Guarantee", path: "/guarantee" },
  { label: "Help & Support",      path: "/help" },
];
const POPULAR_CATEGORIES = [
  { label: "Cleaning",   path: "/services/cleaning" },
  { label: "Plumbing",   path: "/services/plumbing" },
  { label: "Electrical", path: "/services/electrical" },
  { label: "Gardening",  path: "/services/gardening" },
  { label: "Moving",     path: "/services/moving" },
  { label: "Errands",    path: "/services/errands-delivery" },
];

function RightPanel({ navigate }) {
  const [catHov, setCatHov] = useState(null);
  const [ctaHov, setCtaHov] = useState(false);

  return (
    <aside style={{ width: 272, flexShrink: 0, display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.faint, fontFamily: T.sans, margin: "0 0 12px" }}>From JiraniApp</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {TRUST_CARDS.map((card, i) => <TrustCard key={i} card={card} navigate={navigate} />)}
        </div>
      </div>

      <div style={{ background: T.white, border: `1.5px solid ${T.border}`, borderRadius: T.r14, padding: "18px 16px", boxShadow: T.s1 }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.faint, fontFamily: T.sans, margin: "0 0 12px" }}>For clients</p>
        {CLIENT_LINKS.map((link, i) => (
          <div key={i} className="quick-link-row" onClick={() => navigate(link.path)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "9px 0", borderBottom: i < CLIENT_LINKS.length - 1 ? `1px solid ${T.borderFaint}` : "none", cursor: "pointer" }}>
            <span style={{ fontSize: 13, color: T.textMid, fontFamily: T.sans, fontWeight: 400, transition: T.tx }}>{link.label}</span>
            <Icon name="chevronRight" size={12} stroke={T.faint} strokeWidth={2.2} />
          </div>
        ))}
      </div>

      <div style={{ background: T.white, border: `1.5px solid ${T.border}`, borderRadius: T.r14, padding: "18px 16px", boxShadow: T.s1 }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.faint, fontFamily: T.sans, margin: "0 0 12px" }}>Popular categories</p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
          {POPULAR_CATEGORIES.map((cat, i) => (
            <button key={cat.label} onClick={() => navigate(cat.path)} onMouseEnter={() => setCatHov(i)} onMouseLeave={() => setCatHov(null)}
              style={{ padding: "6px 13px", borderRadius: T.rFull, fontSize: 12, fontFamily: T.sans, fontWeight: 500, cursor: "pointer", transition: T.tx, border: `1.5px solid ${catHov === i ? T.green : T.border}`, background: catHov === i ? T.greenTint : T.card2, color: catHov === i ? T.greenDark : T.sub }}>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: `linear-gradient(145deg, ${T.greenDark} 0%, ${T.greenDeep} 100%)`, borderRadius: T.r16, padding: "20px 18px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -20, right: -20, width: 90, height: 90, borderRadius: "50%", background: "rgba(255,255,255,0.06)", pointerEvents: "none" }} />
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.greenTint, fontFamily: T.sans, margin: "0 0 8px" }}>Ready to book?</p>
        <p style={{ fontFamily: T.serif, fontSize: 17, color: T.white, margin: "0 0 6px", lineHeight: 1.3, fontStyle: "italic" }}>Find a trusted pro near you</p>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.72)", fontFamily: T.sans, margin: "0 0 16px", lineHeight: 1.55 }}>Plumbing, cleaning, electrical, gardening and more — booked in minutes.</p>
        <button onClick={() => navigate("/search")} onMouseEnter={() => setCtaHov(true)} onMouseLeave={() => setCtaHov(false)}
          style={{ width: "100%", background: ctaHov ? T.greenTint : T.white, color: T.greenDeep, border: "none", borderRadius: T.r10, padding: "10px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: T.sans, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, transition: T.tx }}>
          <Icon name="search" size={14} stroke={T.greenDeep} strokeWidth={2} /> Find a JiraniPro
        </button>
      </div>
    </aside>
  );
}

/* ─── FormField / TextInput / ToggleSwitch / SaveButton / ConfirmDialog ─── */
function FormField({ label, children, hint, error }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: T.sub, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, display: "flex", alignItems: "center", gap: 8, fontFamily: T.sans }}>{label}</label>
      {children}
      {hint && !error && <p style={{ fontSize: 11, color: T.faint, marginTop: 5, fontFamily: T.sans, lineHeight: 1.5 }}>{hint}</p>}
      {error && <p style={{ fontSize: 11, color: T.red, marginTop: 5, fontFamily: T.sans }}>{error}</p>}
    </div>
  );
}
function TextInput({ value, onChange, placeholder, disabled = false, type = "text", error }) {
  const [focus, setFocus] = useState(false);
  return (
    <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled}
      onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
      style={{ width: "100%", padding: "11px 13px", borderRadius: T.r10, border: `1.5px solid ${error ? T.red : focus ? T.green : T.border}`, fontSize: 13.5, fontFamily: T.sans, color: T.text, background: disabled ? T.card2 : T.white, transition: "border-color 0.15s, box-shadow 0.15s", minHeight: "44px", outline: "none", boxSizing: "border-box", boxShadow: focus && !error ? `0 0 0 3px ${T.greenTint}` : "none" }} />
  );
}
function ToggleSwitch({ checked, onChange, label, description }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, padding: "10px 0" }}>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 13.5, fontWeight: 600, color: T.textMid, margin: "0 0 2px", fontFamily: T.sans }}>{label}</p>
        {description && <p style={{ fontSize: 12, color: T.faint, margin: 0, fontFamily: T.sans, lineHeight: 1.5 }}>{description}</p>}
      </div>
      <button onClick={() => onChange(!checked)} style={{ width: 44, height: 24, borderRadius: 12, cursor: "pointer", background: checked ? T.green : T.ghost, position: "relative", transition: "background 0.2s", flexShrink: 0, border: "none" }}>
        <div style={{ position: "absolute", top: 3, left: checked ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: T.white, boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
      </button>
    </div>
  );
}
function SaveButton({ onClick, saving, savedRecently, disabled = false }) {
  const [showSaved, setShowSaved] = useState(false);
  useEffect(() => { if (savedRecently) { setShowSaved(true); const t = setTimeout(() => setShowSaved(false), 2000); return () => clearTimeout(t); } }, [savedRecently]);
  if (showSaved) return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "11px 22px", background: T.greenTint, border: `1px solid ${T.greenBd}`, borderRadius: T.r10, color: T.greenDark, fontSize: 13.5, fontWeight: 600, fontFamily: T.sans }}>
      <Icon name="check" size={15} stroke={T.greenDark} strokeWidth={2.5} /> Saved
    </div>
  );
  return (
    <button onClick={onClick} disabled={saving || disabled}
      style={{ background: T.green, color: T.white, border: "none", borderRadius: T.r10, padding: "11px 24px", fontSize: 13.5, fontWeight: 700, cursor: saving || disabled ? "default" : "pointer", fontFamily: T.sans, transition: "background 0.15s", minHeight: "44px", opacity: saving ? 0.8 : disabled ? 0.5 : 1, boxShadow: "0 2px 10px rgba(30,146,84,0.2)" }}
      onMouseEnter={e => { if (!saving && !disabled) e.currentTarget.style.background = T.greenDark; }}
      onMouseLeave={e => { if (!saving && !disabled) e.currentTarget.style.background = T.green; }}>
      {saving ? (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" style={{ animation: "spin 0.7s linear infinite" }}><circle cx="12" cy="12" r="10" strokeOpacity="0.25" /><path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" /></svg>
          Saving…
        </span>
      ) : "Save changes"}
    </button>
  );
}
function ConfirmDialog({ open, title, message, confirmLabel, danger = false, onConfirm, onCancel }) {
  const { isMobile } = useBreakpoint();
  if (!open) return null;
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onCancel(); }} style={{ position: "fixed", inset: 0, background: "rgba(15,17,23,0.6)", backdropFilter: "blur(6px)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center", animation: "fadeO 0.2s ease" }}>
      <div style={{ background: T.white, width: "100%", maxWidth: 480, borderRadius: "20px 20px 0 0", padding: isMobile ? "28px 20px 36px" : "32px 32px 40px", maxHeight: "94vh", overflowY: "auto", animation: "slideU 0.26s cubic-bezier(0.4,0,0.2,1)", boxShadow: "0 -4px 40px rgba(0,0,0,0.12)" }}>
        <h3 style={{ fontSize: isMobile ? 17 : 20, fontWeight: 700, margin: "0 0 8px", fontFamily: T.sans, color: danger ? T.red : T.text }}>{title}</h3>
        <p style={{ fontSize: 13.5, color: T.sub, margin: "0 0 28px", fontFamily: T.sans, lineHeight: 1.65 }}>{message}</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ background: "none", border: `1.5px solid ${T.ghost}`, borderRadius: T.r10, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, color: T.textMid }}>Cancel</button>
          <button onClick={onConfirm} style={{ background: danger ? T.red : T.green, border: "none", borderRadius: T.r10, padding: "10px 22px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, color: T.white }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* ─── SectionCard — desktop wrapper (icon + title + description + card) ─── */
function SectionCard({ id, icon, title, description, children }) {
  const { isMobile } = useBreakpoint();
  return (
    <section id={id} data-section-id={id} style={{ background: T.white, border: `1.5px solid ${T.border}`, borderRadius: T.r16, padding: isMobile ? "18px 16px" : "22px 24px", marginBottom: 16, scrollMarginTop: 88, boxShadow: T.s1 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: description ? 6 : 18 }}>
        <div style={{ width: 38, height: 38, borderRadius: T.r10, flexShrink: 0, background: T.greenTint, border: `1px solid ${T.greenBd}`, display: "flex", alignItems: "center", justifyContent: "center", color: T.greenDark }}>
          <Icon name={icon} size={17} strokeWidth={1.8} />
        </div>
        <h2 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 400, margin: 0, fontFamily: T.serif, color: T.text, letterSpacing: "-0.01em", fontStyle: "italic" }}>{title}</h2>
      </div>
      {description && <p style={{ fontSize: isMobile ? 12.5 : 13, color: T.sub, margin: "0 0 18px 50px", fontFamily: T.sans, lineHeight: 1.6 }}>{description}</p>}
      {children}
    </section>
  );
}

/* ─── Mobile accordion row — nothing renders until the person taps it. ─── */
function AccordionSection({ id, icon, label, open, onToggle, children }) {
  return (
    <div style={{ background: T.white, border: `1.5px solid ${T.border}`, borderRadius: T.r16, overflow: "hidden", boxShadow: T.s1, marginBottom: 10 }}>
      <button onClick={onToggle} className="cs-accordion-row" style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", transition: T.tx }}>
        <span style={{ width: 32, height: 32, borderRadius: T.r10, flexShrink: 0, background: open ? T.greenTint : T.card2, border: `1px solid ${open ? T.greenBd : T.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name={icon} size={15} strokeWidth={1.8} stroke={open ? T.greenDark : T.sub} />
        </span>
        <span style={{ flex: 1, fontSize: 13.5, fontWeight: open ? 700 : 500, color: T.text, fontFamily: T.sans }}>{label}</span>
        <span style={{ transition: T.tx, transform: open ? "rotate(180deg)" : "rotate(0deg)", display: "flex" }}>
          <Icon name="chevronDown" size={15} stroke={T.faint} strokeWidth={2.2} />
        </span>
      </button>
      <div style={{ maxHeight: open ? 3000 : 0, overflow: "hidden", transition: "max-height 0.32s cubic-bezier(.4,0,.2,1)" }}>
        <div style={{ padding: "4px 16px 20px" }}>{children}</div>
      </div>
    </div>
  );
}

/* ─── Section content — split from their wrapper so the same fields can
   sit inside a SectionCard (desktop) or an AccordionSection (mobile). ─── */
function ProfileFields({ profile, onUpdateProfile, onAvatarUpload, onAvatarRemove, uploadingAvatar }) {
  const [fullName, setFullName] = useState(profile?.full_name || "");
  const [phone, setPhone] = useState(profile?.phone || "");
  const [email, setEmail] = useState(profile?.email || "");
  const [saving, setSaving] = useState(false);
  const [savedRecently, setSavedRecently] = useState(false);
  const { isMobile } = useBreakpoint();
  const fileInputRef = useRef(null);
  const initials = (profile?.full_name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const emailValid = !email.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  useEffect(() => { setFullName(profile?.full_name || ""); setPhone(profile?.phone || ""); setEmail(profile?.email || ""); }, [profile]);

  const handleSave = async () => {
    if (!profile?.id || !emailValid) return;
    setSaving(true);
    await onUpdateProfile({ full_name: fullName, phone, email: email.trim() });
    setSavedRecently(true); setSaving(false);
    setTimeout(() => setSavedRecently(false), 2000);
  };

  const avatarSize = isMobile ? 68 : 96;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 14 : 20, marginBottom: 24 }}>
        <div style={{ width: avatarSize, height: avatarSize, borderRadius: "50%", background: `linear-gradient(145deg, ${T.green}22, ${T.greenTint})`, border: `3px solid ${T.white}`, boxShadow: "0 4px 20px rgba(30,146,84,0.15)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {profile?.avatar_url ? <img src={profile.avatar_url} alt="Avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: isMobile ? 24 : 34, fontWeight: 700, color: T.greenDark, fontFamily: T.sans }}>{initials}</span>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button onClick={() => fileInputRef.current?.click()} disabled={uploadingAvatar} style={{ background: "none", border: `1.5px solid ${T.ghost}`, borderRadius: T.r10, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, color: T.textMid, display: "flex", alignItems: "center", gap: 6 }}>
            {uploadingAvatar ? (<><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: "spin 0.7s linear infinite" }}><circle cx="12" cy="12" r="10" strokeOpacity="0.25" /><path d="M12 2a10 10 0 0 1 10 10" /></svg>Uploading…</>) : (<><Icon name="edit" size={13} strokeWidth={2} />Change photo</>)}
          </button>
          {profile?.avatar_url && <button onClick={onAvatarRemove} className="remove-photo" style={{ background: "none", border: "none", padding: "2px 0", fontSize: 12, fontFamily: T.sans, color: T.faint, cursor: "pointer", textAlign: "left" }}>Remove photo</button>}
          <input ref={fileInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) onAvatarUpload(f); e.target.value = ''; }} />
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "0 20px" }}>
        <FormField label="Full name"><TextInput value={fullName} onChange={setFullName} placeholder="Your full name" /></FormField>
        <FormField label="Phone number"><TextInput value={phone} onChange={setPhone} placeholder="07XX XXX XXX" type="tel" /></FormField>
        <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
          <FormField label={<span>Email <span style={{ fontWeight: 400, color: T.faint, textTransform: "none" }}>(optional)</span></span>} hint={emailValid ? "Used for booking receipts and account recovery. Changing it sends a confirmation link to the new address." : undefined} error={!emailValid ? "Enter a valid email address" : undefined}>
            <TextInput value={email} onChange={setEmail} placeholder="email@example.com" type="email" error={!emailValid} />
          </FormField>
        </div>
      </div>
      <div style={{ marginTop: 8 }}><SaveButton onClick={handleSave} saving={saving} savedRecently={savedRecently} disabled={!emailValid} /></div>
    </>
  );
}

function SecurityFields({ onPasswordUpdate, mfaEnabled, mfaFactorId, onMfaChange, onRequestSignOutDialog, showToast }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [savedRecently, setSavedRecently] = useState(false);
  const [mfaConfirmOpen, setMfaConfirmOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollData, setEnrollData] = useState(null);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [unenrolling, setUnenrolling] = useState(false);
  const { isMobile } = useBreakpoint();
  const pwdValid = currentPassword.length > 0 && newPassword.length >= 8 && newPassword === confirmPassword;

  const handlePasswordSave = async () => {
    if (!pwdValid) return;
    setSavingPwd(true);
    try {
      await onPasswordUpdate(currentPassword, newPassword);
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      setSavedRecently(true);
      setTimeout(() => setSavedRecently(false), 2000);
    } catch { /* toast already surfaced */ } finally { setSavingPwd(false); }
  };

  const startEnroll = async () => {
    setEnrolling(true);
    try {
      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
      if (error) throw error;
      setEnrollData({ factorId: data.id, qrCode: data.totp.qr_code, secret: data.totp.secret });
    } catch (err) { showToast(err?.message || "Couldn't start two-factor setup.", "error"); setEnrolling(false); }
  };
  const confirmEnroll = async () => {
    if (verifyCode.length !== 6 || !enrollData) return;
    setVerifying(true);
    try {
      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId: enrollData.factorId });
      if (challengeErr) throw challengeErr;
      const { error: verifyErr } = await supabase.auth.mfa.verify({ factorId: enrollData.factorId, challengeId: challenge.id, code: verifyCode });
      if (verifyErr) throw verifyErr;
      onMfaChange(true, enrollData.factorId);
      setEnrolling(false); setEnrollData(null); setVerifyCode("");
      showToast("Two-factor authentication enabled.");
    } catch (err) { showToast(err?.message || "That code didn't work — try again.", "error"); } finally { setVerifying(false); }
  };
  const cancelEnroll = async () => {
    if (enrollData?.factorId) { try { await supabase.auth.mfa.unenroll({ factorId: enrollData.factorId }); } catch { /* best-effort */ } }
    setEnrolling(false); setEnrollData(null); setVerifyCode("");
  };
  const disableMfa = async () => {
    setMfaConfirmOpen(false);
    if (!mfaFactorId) { onMfaChange(false, null); return; }
    setUnenrolling(true);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: mfaFactorId });
      if (error) throw error;
      onMfaChange(false, null);
      showToast("Two-factor authentication disabled.");
    } catch (err) { showToast(err?.message || "Couldn't disable two-factor authentication.", "error"); } finally { setUnenrolling(false); }
  };

  return (
    <>
      <div style={{ paddingBottom: 24, marginBottom: 24, borderBottom: `1px solid ${T.borderFaint}` }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: T.textMid, margin: "0 0 14px", fontFamily: T.sans, textTransform: "uppercase", letterSpacing: "0.06em" }}>Change password</p>
        <FormField label="Current password"><TextInput type="password" value={currentPassword} onChange={setCurrentPassword} placeholder="••••••••" /></FormField>
        <FormField label="New password" error={newPassword && newPassword.length < 8 ? "At least 8 characters required" : undefined}><TextInput type="password" value={newPassword} onChange={setNewPassword} placeholder="New password" /></FormField>
        <FormField label="Confirm new password" error={confirmPassword && newPassword !== confirmPassword ? "Passwords don't match" : undefined}><TextInput type="password" value={confirmPassword} onChange={setConfirmPassword} placeholder="Repeat new password" /></FormField>
        <div style={{ marginTop: 8 }}><SaveButton onClick={handlePasswordSave} saving={savingPwd} savedRecently={savedRecently} /></div>
      </div>

      <div style={{ border: `1px solid ${T.border}`, borderRadius: T.r12, padding: 16, marginBottom: 20 }}>
        <ToggleSwitch checked={mfaEnabled || enrolling} onChange={checked => { if (unenrolling) return; if (checked) startEnroll(); else setMfaConfirmOpen(true); }} label="Two-factor authentication" description="Add an extra layer of security with an authenticator app." />
        {enrolling && enrollData && (
          <div style={{ marginTop: 14, padding: 16, background: T.card2, borderRadius: T.r12, border: `1px dashed ${T.ghost}` }}>
            <div style={{ width: isMobile ? 140 : 160, height: isMobile ? 140 : 160, background: T.white, border: `1px solid ${T.border}`, borderRadius: T.r12, margin: "0 auto 12px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {enrollData.qrCode ? <img src={enrollData.qrCode} alt="Two-factor QR code" style={{ width: "100%", height: "100%", objectFit: "contain" }} /> : <span style={{ fontSize: 12, color: T.faint }}>Loading…</span>}
            </div>
            <p style={{ fontSize: 12, textAlign: "center", color: T.sub, margin: "0 0 4px", fontFamily: T.sans }}>Scan with Google Authenticator or Authy.</p>
            {enrollData.secret && <p style={{ fontSize: 10.5, textAlign: "center", color: T.faint, margin: "0 0 14px", fontFamily: "monospace", wordBreak: "break-all" }}>Or enter manually: {enrollData.secret}</p>}
            <div style={{ maxWidth: 220, margin: "0 auto" }}>
              <FormField label="6-digit code"><TextInput value={verifyCode} onChange={v => setVerifyCode(v.replace(/\D/g, "").slice(0, 6))} placeholder="000000" /></FormField>
              <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                <button onClick={cancelEnroll} style={{ background: "none", border: `1.5px solid ${T.ghost}`, borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, color: T.textMid }}>Cancel</button>
                <button onClick={confirmEnroll} disabled={verifyCode.length !== 6 || verifying} style={{ background: T.green, border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, color: T.white, cursor: verifyCode.length !== 6 ? "default" : "pointer", fontFamily: T.sans, opacity: verifyCode.length !== 6 || verifying ? 0.6 : 1 }}>{verifying ? "Verifying…" : "Confirm"}</button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ background: T.redTint, border: `1px solid ${T.redBd}`, borderRadius: T.r12, padding: 16 }}>
        <p style={{ fontSize: 13, fontWeight: 700, color: T.red, margin: "0 0 4px", fontFamily: T.sans }}>Sign out of all devices</p>
        <p style={{ fontSize: 12, color: T.sub, margin: "0 0 12px", fontFamily: T.sans, lineHeight: 1.5 }}>This will end every active session, including this one.</p>
        <button onClick={onRequestSignOutDialog} style={{ background: "none", border: `1.5px solid ${T.redBd}`, borderRadius: T.r10, padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, color: T.red, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Icon name="logout" size={13} strokeWidth={2} />Sign out everywhere
        </button>
      </div>

      <ConfirmDialog open={mfaConfirmOpen} title="Disable two-factor authentication?" message="You'll lose the extra security layer. Your account will only be protected by your password." confirmLabel="Disable anyway" danger onConfirm={disableMfa} onCancel={() => setMfaConfirmOpen(false)} />
    </>
  );
}

function NotificationsFields({ prefs, onUpdatePrefs }) {
  const [localPrefs, setLocalPrefs] = useState(prefs);
  const [justSavedKey, setJustSavedKey] = useState(null);
  useEffect(() => { setLocalPrefs(prefs); }, [prefs]);

  const toggle = async key => {
    const next = { ...localPrefs, [key]: !localPrefs[key] };
    setLocalPrefs(next);
    await onUpdatePrefs(next);
    setJustSavedKey(key);
    setTimeout(() => setJustSavedKey(k => (k === key ? null : k)), 1500);
  };

  const bookingToggles = [
    { key: "notify_booking_updates", label: "Booking confirmed / accepted", description: "When your booking is confirmed or rescheduled by the pro." },
    { key: "notify_job_status",      label: "Job started / completed",      description: "Real-time updates when work begins and finishes." },
    { key: "notify_messages",        label: "New messages from your Pro",    description: "Get notified when you receive a new message." },
  ];
  const accountToggles = [
    { key: "notify_payments",        label: "Payment receipts and refunds",  description: "Payment confirmations and refund status updates." },
    { key: "notify_promotions",      label: "Tips, offers & promotions",     description: "Special discounts and seasonal offers." },
    { key: "notify_product_updates", label: "Product updates",               description: "New features and platform improvements." },
  ];

  const GroupBlock = ({ label, items }) => (
    <div style={{ border: `1px solid ${T.borderFaint}`, borderRadius: T.r12, padding: 16, marginBottom: 14 }}>
      <p style={{ fontSize: 10.5, fontWeight: 700, color: T.faint, textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 6px", fontFamily: T.sans }}>{label}</p>
      {items.map((item, i) => (
        <React.Fragment key={item.key}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1 }}><ToggleSwitch checked={localPrefs[item.key]} onChange={() => toggle(item.key)} label={item.label} description={item.description} /></div>
            {justSavedKey === item.key && <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, color: T.green, fontFamily: T.sans, flexShrink: 0 }}><Icon name="check" size={12} stroke={T.green} strokeWidth={2.5} /> Saved</span>}
          </div>
          {i < items.length - 1 && <div style={{ height: 1, background: T.borderFaint }} />}
        </React.Fragment>
      ))}
    </div>
  );

  return (<><GroupBlock label="Booking updates" items={bookingToggles} /><GroupBlock label="Account & promotions" items={accountToggles} /></>);
}

function PaymentRefundFields({ mpesaPhone, onUpdateMpesa }) {
  const [phone, setPhone] = useState(mpesaPhone || "");
  const [saving, setSaving] = useState(false);
  const [savedRecently, setSavedRecently] = useState(false);
  const { isMobile } = useBreakpoint();
  useEffect(() => { setPhone(mpesaPhone || ""); }, [mpesaPhone]);

  const handleSave = async () => { setSaving(true); await onUpdateMpesa(phone); setSavedRecently(true); setSaving(false); setTimeout(() => setSavedRecently(false), 2000); };

  const steps = [{ n: 1, text: "Refund approved" }, { n: 2, text: "Sent to this number" }, { n: 3, text: "Arrives via M-Pesa within 24 hrs" }];

  return (
    <>
      <div style={{ background: T.greenTint, borderRadius: T.r12, padding: "14px 16px", marginBottom: 20, border: `1px solid ${T.greenBd}` }}>
        <p style={{ fontSize: 13, color: T.textMid, margin: 0, fontFamily: T.sans, lineHeight: 1.55 }}>Refunds for cancelled or disputed bookings go to this M-Pesa number. Make sure it's correct before saving.</p>
      </div>

      <FormField label={<span style={{ display: "flex", alignItems: "center", gap: 8 }}>M-Pesa phone number <MpesaMark /></span>}>
        <TextInput value={phone} onChange={setPhone} placeholder="07XX XXX XXX" type="tel" />
      </FormField>

      {/* Saved-method row, styled like a stored card/payment method */}
      {mpesaPhone && (
        <div style={{ display: "flex", alignItems: "center", gap: 11, padding: "12px 14px", border: `1.5px solid ${T.greenBd}`, borderRadius: T.r12, marginBottom: 20, background: T.greenTint }}>
          <MpesaMark />
          <div style={{ minWidth: 0, flex: 1 }}>
            <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: T.text, fontFamily: T.sans }}>M-Pesa</p>
            <p style={{ margin: "1px 0 0", fontSize: 11.5, color: T.faint, fontFamily: "monospace", letterSpacing: "0.02em" }}>{mpesaPhone.replace(/\D/g, "").match(/.{1,4}/g)?.join("  ") || mpesaPhone}</p>
          </div>
          <span style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${T.green}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <span style={{ width: 9, height: 9, borderRadius: "50%", background: T.green }} />
          </span>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center", gap: isMobile ? 10 : 0, margin: "4px 0 20px" }}>
        {steps.map((step, i) => (
          <React.Fragment key={step.n}>
            <div style={{ display: "flex", alignItems: isMobile ? "center" : "flex-start", flexDirection: isMobile ? "row" : "column", gap: isMobile ? 10 : 8 }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0, background: T.greenTint, border: `1px solid ${T.greenBd}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: T.greenDark, fontFamily: T.sans }}>{step.n}</div>
              <p style={{ fontSize: 11.5, color: T.sub, margin: 0, fontFamily: T.sans, lineHeight: 1.4, maxWidth: isMobile ? "none" : 90, textAlign: isMobile ? "left" : "center" }}>{step.text}</p>
            </div>
            {!isMobile && i < steps.length - 1 && <div style={{ flex: 1, height: 1, background: T.border, margin: "0 10px", alignSelf: "flex-start", marginTop: 12 }} />}
          </React.Fragment>
        ))}
      </div>

      <SaveButton onClick={handleSave} saving={saving} savedRecently={savedRecently} />
    </>
  );
}

function AddressesFields({ addresses, onAddAddress, onUpdateAddress, onDeleteAddress }) {
  const [editingId, setEditingId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const { isMobile } = useBreakpoint();

  const confirmDelete = async () => { if (deleteConfirm) await onDeleteAddress(deleteConfirm); setDeleteConfirm(null); };

  const AddressForm = ({ address, onSubmit, onCancel, isNew = false }) => {
    const [label, setLabel] = useState(address?.label || "");
    const [addressLine, setAddressLine] = useState(address?.address_line || "");
    const [locationArea, setLocationArea] = useState(address?.location_area || "");
    const [isDefault, setIsDefault] = useState(address?.is_default || false);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      if (!label.trim() || !addressLine.trim()) return;
      setSubmitting(true);
      await onSubmit({ id: address?.id, label, address_line: addressLine, location_area: locationArea, is_default: isDefault });
      setSubmitting(false); onCancel();
    };

    return (
      <div style={{ marginTop: 14, padding: isMobile ? 16 : 18, background: T.card2, borderRadius: T.r12, border: `1px solid ${T.border}` }}>
        <FormField label="Label (e.g. Home, Work)"><TextInput value={label} onChange={setLabel} placeholder="Home" /></FormField>
        <FormField label="Address line"><TextInput value={addressLine} onChange={setAddressLine} placeholder="Full street address" /></FormField>
        <FormField label="Area / neighbourhood" hint="e.g. Westlands, Nyali, Kilimani"><TextInput value={locationArea} onChange={setLocationArea} placeholder="Neighbourhood" /></FormField>
        <label style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 0 14px", cursor: "pointer" }}>
          <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} style={{ accentColor: T.green }} />
          <span style={{ fontSize: 13, fontFamily: T.sans, color: T.textMid }}>Set as default address</span>
        </label>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ background: "none", border: `1.5px solid ${T.ghost}`, borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: T.sans, color: T.textMid }}>Cancel</button>
          <button onClick={handleSubmit} disabled={submitting} style={{ background: T.green, border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, color: T.white, cursor: "pointer", fontFamily: T.sans }}>{submitting ? "Saving…" : isNew ? "Add address" : "Save changes"}</button>
        </div>
      </div>
    );
  };

  return (
    <>
      {addresses.length === 0 && !adding && (
        <div style={{ textAlign: "center", padding: "28px 16px", color: T.faint, border: `1px dashed ${T.ghost}`, borderRadius: T.r12, marginBottom: 14 }}>
          <Icon name="mapPin" size={28} strokeWidth={1.5} />
          <p style={{ margin: "8px 0 0", fontSize: 13, fontFamily: T.sans }}>No saved addresses yet</p>
        </div>
      )}

      {addresses.map(addr => (
        <div key={addr.id}>
          {editingId === addr.id ? (
            <AddressForm address={addr} onSubmit={async updated => { await onUpdateAddress(updated); setEditingId(null); }} onCancel={() => setEditingId(null)} />
          ) : (
            <div className="addr-card" onClick={() => !addr.is_default && onUpdateAddress({ ...addr, is_default: true })}
              style={{ border: `1.5px solid ${addr.is_default ? T.green : T.border}`, background: addr.is_default ? T.greenTint : T.white, borderRadius: T.r12, padding: "14px 16px", marginBottom: 10, display: "flex", alignItems: "flex-start", gap: 12, transition: T.tx, cursor: addr.is_default ? "default" : "pointer" }}>
              <span style={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0, marginTop: 2, border: `2px solid ${addr.is_default ? T.green : T.ghost}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {addr.is_default && <span style={{ width: 9, height: 9, borderRadius: "50%", background: T.green }} />}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.sans }}>{addr.label}</span>
                  {addr.is_default && <Badge variant="green" size="sm">Default</Badge>}
                </div>
                <p style={{ margin: 0, fontSize: 13, color: T.sub, fontFamily: T.sans, lineHeight: 1.5 }}>{addr.address_line}</p>
                {addr.location_area && <p style={{ margin: "2px 0 0", fontSize: 11.5, color: T.faint, fontFamily: T.sans }}>{addr.location_area}</p>}
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                <button onClick={() => setEditingId(addr.id)} style={{ width: 28, height: 28, borderRadius: 8, background: T.white, border: `1px solid ${T.border}`, cursor: "pointer", color: T.sub, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="edit" size={13} strokeWidth={1.8} /></button>
                <button onClick={() => setDeleteConfirm(addr.id)} style={{ width: 28, height: 28, borderRadius: 8, background: T.white, border: `1px solid ${T.border}`, cursor: "pointer", color: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="trash" size={13} strokeWidth={1.8} /></button>
              </div>
            </div>
          )}
        </div>
      ))}

      {adding && <AddressForm isNew onSubmit={async newAddr => { await onAddAddress(newAddr); setAdding(false); }} onCancel={() => setAdding(false)} />}

      <button onClick={() => setAdding(true)} style={{ width: "100%", marginTop: 10, padding: "12px", background: "none", border: `2px dashed ${T.ghost}`, borderRadius: T.r12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, color: T.sub, fontSize: 13, fontWeight: 600, fontFamily: T.sans, transition: "all 0.15s" }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = T.green; e.currentTarget.style.color = T.greenDark; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = T.ghost; e.currentTarget.style.color = T.sub; }}>
        <Icon name="plus" size={15} strokeWidth={2} /> Add new address
      </button>

      <ConfirmDialog open={!!deleteConfirm} title="Delete this address?" message="This can't be undone. The address will be permanently removed from your account." confirmLabel="Delete" danger onConfirm={confirmDelete} onCancel={() => setDeleteConfirm(null)} />
    </>
  );
}

/* ─── Recommendation rails (kept, retokenized) ─── */
const CATEGORY_TINTS = {
  Cleaning: T.green, Plumbing: T.blue, Electrical: T.amber, "Hair & Beauty": "#db2777",
  "Home Tutoring": "#0891b2", Photography: T.sub, Gardening: "#16a34a", Carpentry: T.amber,
  Moving: T.text, Childcare: "#e11d48", Fitness: "#ea580c", "Auto Repair": T.text, Errands: T.greenDark,
};
const SERVICE_CATALOG = [
  { id: "c1",  category: "Cleaning",   title: "Full home deep clean",   proName: "Wanjiku M.",   location: "Kilimani, Nairobi",  rating: 4.9, reviews: 214, price: "From KES 2,500", path: "/services/cleaning" },
  { id: "c2",  category: "Cleaning",   title: "Sofa & carpet cleaning", proName: "Otieno Cleaners", location: "Nyali, Mombasa",  rating: 4.7, reviews: 98,  price: "From KES 1,800", path: "/services/cleaning" },
  { id: "c3",  category: "Plumbing",   title: "Leaking pipe repair",    proName: "James K.",     location: "Westlands, Nairobi", rating: 4.8, reviews: 156, price: "From KES 1,200", path: "/services/plumbing" },
  { id: "c5",  category: "Electrical", title: "Full house rewiring",    proName: "Kamau Electricals", location: "Karen, Nairobi", rating: 4.9, reviews: 132, price: "From KES 8,000", path: "/services/electrical" },
  { id: "c8",  category: "Hair & Beauty", title: "Home braiding session", proName: "Beauty by Faith", location: "Nyeri Town", rating: 4.9, reviews: 176, price: "From KES 1,000", path: "/services/hair-beauty" },
  { id: "c12", category: "Gardening",  title: "Garden landscaping",     proName: "GreenScape Kenya", location: "Runda, Nairobi", rating: 4.7, reviews: 54, price: "From KES 3,000", path: "/services/gardening" },
  { id: "c15", category: "Moving",     title: "House moving (3-bedroom)", proName: "Swift Movers KE", location: "South B, Nairobi", rating: 4.7, reviews: 118, price: "From KES 9,000", path: "/services/moving" },
  { id: "c17", category: "Fitness",    title: "Home personal training", proName: "Coach Brian", location: "Kileleshwa, Nairobi", rating: 5.0, reviews: 66, price: "From KES 1,500/session", path: "/services/fitness" },
  { id: "c19", category: "Errands",    title: "Grocery & pharmacy runs", proName: "QuickHands Errands", location: "Nakuru Town", rating: 4.8, reviews: 57, price: "From KES 400", path: "/services/errands-delivery" },
].map(item => ({ ...item, tint: CATEGORY_TINTS[item.category] || T.green }));

function shuffleArray(arr) { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }
function fmtKES(n) { return `KES ${n.toLocaleString("en-KE")}`; }

function ProductCard({ item, navigate }) {
  const { isMobile } = useBreakpoint();
  const [imgErr, setImgErr] = useState(false);
  const [hov, setHov] = useState(false);
  const showImg = item.img && !imgErr;
  // Mobile: narrow, Jumia-rail-style cards — roughly 4-5 fit per screen
  // width instead of the ~2 the desktop-sized 188px card allowed.
  const cardW = isMobile ? 108 : 188;
  const imgH  = isMobile ? 86 : 132;
  return (
    <div onClick={() => navigate(item.path)} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ flex: `0 0 ${cardW}px`, scrollSnapAlign: "start", cursor: "pointer", borderRadius: isMobile ? T.r10 : T.r16, overflow: "hidden", background: T.white, border: `1px solid ${hov ? T.greenBd : T.border}`, boxShadow: hov ? "0 10px 26px rgba(15,17,23,0.10)" : T.s1, transition: T.tx }}>
      <div style={{ position: "relative", width: "100%", height: imgH, background: showImg ? "#eee" : `linear-gradient(155deg, ${item.tint}26 0%, ${item.tint}4a 100%)`, overflow: "hidden" }}>
        {showImg ? <img src={item.img} alt={item.title} onError={() => setImgErr(true)} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s ease", transform: hov ? "scale(1.06)" : "scale(1)" }} /> : (
          <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="zap" size={isMobile ? 18 : 26} stroke={item.tint} strokeWidth={1.6} /></div>
        )}
        {item.badge && <span style={{ position: "absolute", top: isMobile ? 5 : 8, left: isMobile ? 5 : 8, background: "rgba(15,17,23,0.82)", color: "#fff", fontSize: isMobile ? 8.5 : 10, fontWeight: 700, padding: isMobile ? "2px 6px" : "3px 8px", borderRadius: T.rFull, fontFamily: T.sans }}>{item.badge}</span>}
      </div>
      <div style={{ padding: isMobile ? "6px 7px 8px" : "10px 12px 12px" }}>
        <p style={{ fontSize: isMobile ? 11 : 13, fontWeight: 700, color: T.text, margin: isMobile ? "0 0 2px" : "0 0 3px", fontFamily: T.sans, lineHeight: 1.25, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</p>
        {!isMobile && <p style={{ fontSize: 11, color: T.faint, margin: "0 0 6px", fontFamily: T.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.proName} · {item.location}</p>}
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: item.price ? (isMobile ? 3 : 5) : 0 }}>
          <Icon name="star" size={isMobile ? 9 : 11} stroke={T.gold} strokeWidth={0} />
          <span style={{ fontSize: isMobile ? 10 : 11.5, fontWeight: 600, color: T.textMid, fontFamily: T.sans }}>{item.rating}</span>
          {!isMobile && item.reviews != null && <span style={{ fontSize: 11, color: T.faint, fontFamily: T.sans }}>({item.reviews})</span>}
        </div>
        {item.price && <p style={{ fontSize: isMobile ? 10.5 : 12, fontWeight: 700, color: T.greenDark, margin: 0, fontFamily: T.sans, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.price}</p>}
      </div>
    </div>
  );
}
function ProductCardsSkeletonRow() {
  const { isMobile } = useBreakpoint();
  const cardW = isMobile ? 108 : 188;
  const imgH  = isMobile ? 86 : 132;
  return (
    <div style={{ display: "flex", gap: isMobile ? 8 : 12, overflow: "hidden" }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} style={{ flex: `0 0 ${cardW}px` }}>
          <div style={{ height: imgH, borderRadius: isMobile ? T.r10 : T.r16, background: "linear-gradient(90deg, #F0F0EE 25%, #E8E8E4 50%, #F0F0EE 75%)", backgroundSize: "200% 100%", animation: "csPulse 1.4s infinite", marginBottom: 8 }} />
          <div style={{ height: 12, width: "80%", borderRadius: 4, background: T.card2, marginBottom: 6 }} />
          <div style={{ height: 10, width: "55%", borderRadius: 4, background: T.card2 }} />
        </div>
      ))}
    </div>
  );
}

/* ─── PromoBanner — Jumia-style bold deals header above the "Near you"
   rail. Rename PROMO_TITLE/PROMO_SUBTITLE to whatever campaign name
   JiraniApp settles on (a "Black Friday"-style equivalent). ─── */
const PROMO_TITLE = "JiraniApp Weekly Deals";
const PROMO_SUBTITLE = "Discounted rates on top-rated pros — refreshed every week";
function PromoBanner({ navigate }) {
  const { isMobile } = useBreakpoint();
  return (
    <div onClick={() => navigate("/search")} style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, cursor: "pointer",
      background: `linear-gradient(120deg, ${T.greenDeep} 0%, ${T.greenDark} 55%, ${T.green} 130%)`,
      borderRadius: isMobile ? T.r12 : T.r16, padding: isMobile ? "14px 16px" : "18px 24px",
      marginBottom: isMobile ? 14 : 20, position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: -30, right: -20, width: 120, height: 120, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,255,255,0.14), transparent 70%)", pointerEvents: "none" }} />
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 14, position: "relative", minWidth: 0 }}>
        <span style={{ width: isMobile ? 34 : 40, height: isMobile ? 34 : 40, borderRadius: "50%", background: "rgba(255,255,255,0.16)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Icon name="zap" size={isMobile ? 16 : 19} stroke="#fff" strokeWidth={2} />
        </span>
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: isMobile ? 14.5 : 18, fontWeight: 800, color: "#fff", fontFamily: T.sans, letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{PROMO_TITLE}</p>
          {!isMobile && <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "rgba(255,255,255,0.75)", fontFamily: T.sans }}>{PROMO_SUBTITLE}</p>}
        </div>
      </div>
      <span style={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", width: isMobile ? 26 : 30, height: isMobile ? 26 : 30, borderRadius: "50%", background: "rgba(255,255,255,0.16)" }}>
        <Icon name="arrowRight" size={isMobile ? 13 : 15} stroke="#fff" strokeWidth={2.2} />
      </span>
    </div>
  );
}
function ProductCardsSection({ user, navigate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [personalized, setPersonalized] = useState(false);
  const scrollRef = useRef(null);

  const loadRecommendations = useCallback(async () => {
    setLoading(true);
    let bookedBefore = [];
    try {
      if (user?.id) {
        const { data, error } = await supabase.from('job_requests').select('id, description, location, pro_id, profiles:pro_id (full_name, avatar_url)').eq('customer_id', user.id).order('created_at', { ascending: false }).limit(10);
        if (!error && data?.length) {
          bookedBefore = data.map(r => ({ id: `booked-${r.id}`, title: r.description || "Service you booked", proName: r.profiles?.full_name || "JiraniPro", location: r.location || "Near you", rating: 4.8, reviews: null, price: null, img: r.profiles?.avatar_url || null, tint: T.green, badge: "Book again", path: r.pro_id ? `/search?pro=${r.pro_id}` : "/search" }));
        }
      }
    } catch (err) { console.warn("Recommendation query skipped:", err?.message); }

    const seenPros = new Set();
    const dedupedBooked = bookedBefore.filter(b => { if (seenPros.has(b.proName)) return false; seenPros.add(b.proName); return true; }).slice(0, 6);
    const fillerCount = Math.max(12 - dedupedBooked.length, 0);
    const filler = shuffleArray(SERVICE_CATALOG).slice(0, fillerCount);
    setItems([...dedupedBooked, ...filler]);
    setPersonalized(dedupedBooked.length > 0);
    setLoading(false);
  }, [user]);

  useEffect(() => { loadRecommendations(); }, [loadRecommendations]);
  const scrollBy = dir => scrollRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });
  const { isMobile } = useBreakpoint();

  return (
    <section style={{ marginTop: 44 }}>
      <PromoBanner navigate={navigate} />

      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.green, fontFamily: T.sans, margin: "0 0 6px" }}>{personalized ? "Based on your bookings" : "Near you"}</p>
          <h2 style={{ fontFamily: T.serif, fontSize: 22, fontWeight: 400, fontStyle: "italic", color: T.text, margin: 0, letterSpacing: "-0.01em" }}>{personalized ? "Book your regulars again" : "Popular services near you"}</h2>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button onClick={loadRecommendations} title="Show different picks" style={{ width: 34, height: 34, borderRadius: "50%", border: `1.5px solid ${T.border}`, background: T.white, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="refresh" size={14} stroke={T.sub} strokeWidth={2} /></button>
          <button onClick={() => scrollBy(-1)} style={{ width: 34, height: 34, borderRadius: "50%", border: `1.5px solid ${T.border}`, background: T.white, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="chevronLeft" size={14} stroke={T.sub} strokeWidth={2.2} /></button>
          <button onClick={() => scrollBy(1)} style={{ width: 34, height: 34, borderRadius: "50%", border: `1.5px solid ${T.border}`, background: T.white, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="chevronRight" size={14} stroke={T.sub} strokeWidth={2.2} /></button>
        </div>
      </div>
      {loading ? <ProductCardsSkeletonRow /> : (
        <div ref={scrollRef} className="cs-scroll" style={{ display: "flex", gap: isMobile ? 8 : 12, overflowX: "auto", scrollSnapType: "x mandatory", paddingBottom: 8 }}>
          {items.map(item => <ProductCard key={item.id} item={item} navigate={navigate} />)}
        </div>
      )}
    </section>
  );
}

/* ═══════════════════════════════════════════════
   FOOTER — trimmed, trust-forward, customer-only.
   No "search for a pro" / "become a pro" CTA band, no Business or
   For Professionals columns. Just the categories and destinations a
   customer actually needs, plus the brand-trust signals (address,
   payments, guarantee) that make it read like Uber/Bolt/Jumia rather
   than a generic site footer. Categories list mirrors the full
   15-item catalog used by the main site Footer.jsx — not a trimmed
   subset — so this footer and the marketing footer never drift out
   of sync. Font sizes step down on mobile so 15 links can fit
   comfortably without overflowing the column.
═══════════════════════════════════════════════ */
const FOOTER_CATEGORIES = [
  { label: "Cleaning & Laundry",      path: "/services/cleaning" },
  { label: "Jirani Chef",             path: "/services/chef" },
  { label: "Plumbing",                path: "/services/plumbing" },
  { label: "Electrical",              path: "/services/electrical" },
  { label: "TV & Electronics Repair", path: "/services/electronics-repair" },
  { label: "Hair & Beauty",           path: "/services/hair-beauty" },
  { label: "Home Tutoring",           path: "/services/home-tutoring" },
  { label: "Photography",             path: "/services/photography" },
  { label: "Gardening",               path: "/services/gardening" },
  { label: "Carpentry & Furniture",   path: "/services/carpentry-furniture" },
  { label: "Moving & Relocation",     path: "/services/moving" },
  { label: "Babysitting & Childcare", path: "/services/childcare" },
  { label: "Fitness & Training",      path: "/services/fitness" },
  { label: "Auto Repair",             path: "/services/auto-repair" },
  { label: "Errands & Delivery",      path: "/services/errands-delivery" },
];
const FOOTER_CLIENT_LINKS = [
  { label: "How JiraniApp Works", path: "/how-it-works" },
  { label: "Customer Reviews",    path: "/customer-reviews" },
  { label: "JiraniApp Guarantee", path: "/guarantee" },
  { label: "Help & Support",      path: "/help" },
];
const FOOTER_COMPANY_LINKS = [
  { label: "About JiraniApp",  path: "/about" },
  { label: "Trust & Safety",   path: "/trust-and-safety" },
  { label: "Terms of Service", path: "/terms" },
  { label: "Privacy Policy",   path: "/privacy" },
];
const FOOTER_SOCIALS = [
  { name: "TikTok", url: "https://www.tiktok.com/@jiraniapp", d: "M9 3v10.55A4 4 0 1 0 13 17V7h4a4 4 0 0 1-4-4z" },
  { name: "Instagram", url: "https://www.instagram.com/jiraniapp", d: "M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.975.975 1.246 2.242 1.308 3.608.058 1.265.07 1.645.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.975.975-2.242 1.246-3.608 1.308-1.265.058-1.645.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.975-.975-1.246-2.242-1.308-3.608C2.175 15.584 2.163 15.204 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608C4.516 2.497 5.783 2.225 7.15 2.233 8.415 2.175 8.795 2.163 12 2.163zm0-2.163C8.741 0 8.332.014 7.052.072 5.197.157 3.355.673 2.014 2.014.673 3.355.157 5.197.072 7.052.014 8.332 0 8.741 0 12c0 3.259.014 3.668.072 4.948.085 1.855.601 3.697 1.942 5.038 1.341 1.341 3.183 1.857 5.038 1.942C8.332 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 1.855-.085 3.697-.601 5.038-1.942 1.341-1.341 1.857-3.183 1.942-5.038.058-1.28.072-1.689.072-4.948 0-3.259-.014-3.668-.072-4.948-.085-1.855-.601-3.697-1.942-5.038C20.645.673 18.803.157 16.948.072 15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" },
  { name: "X", url: "https://twitter.com/jiraniapp", d: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.261 5.635 5.903-5.635zm-1.161 17.52h1.833L7.084 4.126H5.117z" },
  { name: "Facebook", url: "https://www.facebook.com/jiraniapp", d: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" },
];

const JIRANI_ADDRESS = "Delta Corner, Chiromo Road, Westlands, Nairobi";

/* ─── Mobile accordion section — mirrors Footer.jsx's MobileSection ─── */
function MobileFooterSection({ heading, links, open, onToggle, navigate, dark }) {
  return (
    <div style={{ borderBottom: `1px solid ${dark.border}` }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 0", background: "none", border: "none", cursor: "pointer", fontFamily: T.sans,
        }}
      >
        <span style={{ fontSize: 11.5, fontWeight: 700, color: dark.onDark, letterSpacing: ".03em", textTransform: "uppercase" }}>
          {heading}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={dark.onDarkSub} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: T.tx }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      <div style={{ maxHeight: open ? 600 : 0, overflow: "hidden", transition: "max-height 0.28s cubic-bezier(.4,0,.2,1)" }}>
        <ul style={{
          listStyle: "none", margin: 0, padding: "0 0 14px",
          display: links.length > 8 ? "grid" : "block",
          gridTemplateColumns: links.length > 8 ? "1fr 1fr" : "none",
          columnGap: links.length > 8 ? 16 : 0,
        }}>
          {links.map((link, j) => (
            <li key={j} style={{ marginBottom: 7 }}>
              <span onClick={() => navigate(link.path)} className="cs-foot-link"
                style={{ fontSize: 12, color: dark.onDarkSub, cursor: "pointer", lineHeight: 1.4, fontFamily: T.sans, transition: T.tx, display: "inline-block" }}>
                {link.label}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function CustomerFooter({ navigate }) {
  const { isMobile } = useBreakpoint();
  const dark = { bg: "#0d2e1a", card: "#163322", border: "#1e4a2e", border2: "#244f32", onDark: "#FFFFFF", onDarkSub: "rgba(255,255,255,0.55)", onDarkFaint: "rgba(255,255,255,0.28)" };
  const [openSections, setOpenSections] = useState({});
  const toggle = i => setOpenSections(prev => ({ ...prev, [i]: !prev[i] }));

  const FOOTER_COLUMNS = [
    { heading: "Categories", links: FOOTER_CATEGORIES },
    { heading: "For Clients", links: FOOTER_CLIENT_LINKS },
    { heading: "Company", links: FOOTER_COMPANY_LINKS },
  ];

  const FootCol = ({ heading, links }) => (
    <div>
      <p style={{ fontSize: 10.5, fontWeight: 700, color: dark.onDarkFaint, textTransform: "uppercase", letterSpacing: ".1em", margin: "0 0 14px", fontFamily: T.sans }}>{heading}</p>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {links.map((link, j) => (
          <li key={j} style={{ marginBottom: 9 }}>
            <span onClick={() => navigate(link.path)} className="cs-foot-link" style={{ fontSize: 13, color: dark.onDarkSub, cursor: "pointer", lineHeight: 1.4, fontFamily: T.sans, transition: T.tx, display: "inline-block" }}>{link.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer style={{ background: dark.bg, fontFamily: T.sans, borderRadius: T.r20, overflow: "hidden", marginTop: 56, position: "relative" }}>
      <div style={{ position: "absolute", top: -100, left: -100, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(30,146,84,.14) 0%, transparent 70%)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "8px 20px 0" : "40px 40px 8px", position: "relative" }}>
        {isMobile ? (
          <div style={{ marginBottom: 8 }}>
            {FOOTER_COLUMNS.map((col, i) => (
              <MobileFooterSection
                key={i}
                heading={col.heading}
                links={col.links}
                open={!!openSections[i]}
                onToggle={() => toggle(i)}
                navigate={navigate}
                dark={dark}
              />
            ))}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(140px, 1fr))", gap: "0 40px", marginBottom: 36 }}>
            <FootCol heading="Categories" links={FOOTER_CATEGORIES} />
            <FootCol heading="For Clients" links={FOOTER_CLIENT_LINKS} />
            <FootCol heading="Company" links={FOOTER_COMPANY_LINKS} />
          </div>
        )}
      </div>

      {/* Bottom bar — unchanged */}
      <div style={{ borderTop: `1px solid ${dark.border}`, padding: isMobile ? "16px 14px" : "20px 40px", position: "relative" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", justifyContent: "space-between", gap: isMobile ? 14 : 0, flexWrap: "nowrap" }}>
          <div style={{ display: "flex", flexDirection: isMobile ? "row" : "column", alignItems: isMobile ? "center" : "flex-start", justifyContent: isMobile ? "space-between" : "flex-start", gap: isMobile ? 8 : 3 }}>
            <div onClick={() => navigate("/")} style={{ cursor: "pointer", flexShrink: 0 }}>
              <AppLogo variant="light" size={isMobile ? 11 : 15} />
            </div>
            {!isMobile && <span style={{ fontFamily: T.serif, fontStyle: "italic", fontSize: 12.5, color: dark.onDarkSub, whiteSpace: "nowrap" }}>Right next to you</span>}
            {isMobile && (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {FOOTER_SOCIALS.map((s, i) => (
                  <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" title={s.name} style={{ color: dark.onDarkFaint, display: "inline-flex", alignItems: "center", transition: T.tx }}>
                    <svg viewBox="0 0 24 24" width={12} height={12} fill="currentColor"><path d={s.d} /></svg>
                  </a>
                ))}
              </div>
            )}
          </div>

          {!isMobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 16, flex: 1, justifyContent: "center" }}>
              {FOOTER_SOCIALS.map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" title={s.name} style={{ color: dark.onDarkFaint, display: "inline-flex", alignItems: "center", transition: T.tx }}
                  onMouseEnter={e => e.currentTarget.style.color = T.greenBd} onMouseLeave={e => e.currentTarget.style.color = dark.onDarkFaint}>
                  <svg viewBox="0 0 24 24" width={15} height={15} fill="currentColor"><path d={s.d} /></svg>
                </a>
              ))}
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", alignItems: isMobile ? "flex-start" : "flex-end", gap: 2, flex: isMobile ? "none" : 1 }}>
            <span style={{ fontSize: isMobile ? 10 : 11.5, color: dark.onDarkFaint, fontFamily: T.sans, textAlign: isMobile ? "left" : "right", whiteSpace: "nowrap" }}>
              © JiraniApp Ltd. 2025
            </span>
            <span style={{ fontSize: isMobile ? 9.5 : 11.5, color: dark.onDarkFaint, fontFamily: T.sans, textAlign: isMobile ? "left" : "right" }}>
              {JIRANI_ADDRESS}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ═══════════════════════════════════════════════
   SIDEBAR — persistent rail, matches ProDashboardHome.Sidebar exactly
   in structure: logo, nav, sign out. (No membership/identity card —
   removed per product decision; the logo sits directly above the nav.)
═══════════════════════════════════════════════ */
const SECTIONS_CONFIG = [
  { id: "profile",       label: "Profile",       icon: "user" },
  { id: "security",      label: "Security",      icon: "shield" },
  { id: "notifications", label: "Notifications", icon: "bell" },
  { id: "payment",       label: "Payment",       icon: "wallet" },
  { id: "addresses",     label: "Addresses",     icon: "mapPin" },
];

function DesktopSidebar({ profile, activeSection, onNavigate, onSignOut }) {
  const navigate = useNavigate();
  return (
    <div style={{ width: 250, flexShrink: 0, background: T.card2, borderRight: `1px solid ${T.border}`, height: "100vh", position: "sticky", top: 0, display: "flex", flexDirection: "column", padding: "18px 12px 14px" }}>
      <div onClick={() => navigate("/")} style={{ cursor: "pointer", padding: "2px 8px 20px" }}><AppLogo /></div>

      <div style={{ fontSize: 10.5, fontWeight: 700, color: T.faint, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: T.sans, padding: "0 8px", marginBottom: 8 }}>Your account</div>
      <div className="cs-scroll" style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
        {SECTIONS_CONFIG.map(s => {
          const active = activeSection === s.id;
          return (
            <button key={s.id} className={!active ? "cs-nav-item" : ""} onClick={() => onNavigate(s.id)}
              style={{ width: "100%", padding: "9px 12px", display: "flex", alignItems: "center", gap: 11, borderRadius: T.r10, fontFamily: T.sans, cursor: "pointer", transition: T.tx, border: "none", background: active ? T.white : "transparent", boxShadow: active ? T.s1 : "none", margin: "1px 0" }}>
              <span style={{ width: 30, height: 30, borderRadius: T.r10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: active ? T.greenTint : T.white, border: `1px solid ${active ? T.greenBd : T.border}` }}>
                <Icon name={s.icon} size={14} strokeWidth={1.9} stroke={active ? T.green : T.faint} />
              </span>
              <span style={{ flex: 1, textAlign: "left", fontSize: 13, fontWeight: active ? 700 : 500, color: active ? T.text : T.sub }}>{s.label}</span>
              {active && <Icon name="chevronRight" size={13} stroke={T.green} strokeWidth={2} />}
            </button>
          );
        })}
      </div>

      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 8 }}>
        <button onClick={() => navigate("/find-a-jiranipro")} className="cs-nav-item" style={{ width: "100%", padding: "9px 12px", display: "flex", alignItems: "center", gap: 11, borderRadius: T.r10, fontFamily: T.sans, cursor: "pointer", border: "none", background: "transparent" }}>
          <span style={{ width: 30, height: 30, borderRadius: T.r10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: T.blueTint, border: `1px solid ${T.blueBd}` }}><Icon name="search" size={14} strokeWidth={1.9} stroke={T.blue} /></span>
          <span style={{ flex: 1, textAlign: "left", fontSize: 13, fontWeight: 500, color: T.sub }}>Find a JiraniPro</span>
        </button>
        <button onClick={onSignOut} className="cs-nav-item" style={{ width: "100%", padding: "9px 12px", display: "flex", alignItems: "center", gap: 11, borderRadius: T.r10, fontFamily: T.sans, cursor: "pointer", border: "none", background: "transparent" }}>
          <span style={{ width: 30, height: 30, borderRadius: T.r10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: T.redTint, border: `1px solid ${T.redBd}` }}><Icon name="logout" size={14} strokeWidth={1.9} stroke={T.red} /></span>
          <span style={{ flex: 1, textAlign: "left", fontSize: 13, fontWeight: 600, color: T.red }}>Sign out</span>
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   TOP BAR — fused to the sidebar column so it reads as one shell.
   Messages/Notifications are now MessagesMenu/NotificationsMenu,
   defined earlier in this same file — no external imports. Mobile
   logo is sized to match the homepage nav-bar's small logo (size 11)
   instead of the larger desktop-sidebar scale.
═══════════════════════════════════════════════ */
function TopBar({ profile, initials, navigate, user, onSignOutRequest, onJumpToSection, search, setSearch }) {
  const { isMobile } = useBreakpoint();
  const [searchFocused, setSearchFocused] = useState(false);

  return (
    <div style={{ height: isMobile ? 56 : 66, borderBottom: isMobile ? "none" : `1px solid ${T.borderFaint}`, padding: isMobile ? "0 16px" : "0 26px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, background: T.white, position: "sticky", top: 0, zIndex: 200 }}>
      {isMobile ? (
        <div onClick={() => navigate("/")} style={{ cursor: "pointer" }}><AppLogo size="sm" /></div>
      ) : (
        <div style={{ flex: 1, maxWidth: 340, position: "relative" }}>
          <span style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: searchFocused ? T.green : T.ghost, display: "flex" }}><Icon name="search" size={14} strokeWidth={2.2} /></span>
          <input value={search} onChange={e => setSearch(e.target.value)} onFocus={() => setSearchFocused(true)} onBlur={() => setSearchFocused(false)}
            onKeyDown={e => { if (e.key === "Enter" && search.trim()) navigate(`/search?q=${encodeURIComponent(search.trim())}`); }}
            placeholder="Search services…"
            style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px 9px 34px", borderRadius: T.rFull, border: `1.5px solid ${searchFocused ? T.greenBd : T.border}`, background: searchFocused ? T.white : T.card2, fontSize: 12.5, fontFamily: T.sans, color: T.text, outline: "none", transition: T.tx }} />
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 4 : 6, flexShrink: 0 }}>
        <MessagesMenu user={user} navigate={navigate} />
        <NotificationsMenu user={user} navigate={navigate} />
        <ProfileMenu profile={profile} initials={initials} navigate={navigate} onSignOut={onSignOutRequest} onJumpToSection={onJumpToSection} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════ */
export default function CustomerSettingsPage() {
  const navigate = useNavigate();
  const { user, isLoggedIn, logoutUser } = useAuth();
  const { open: openLogin } = useLoginModal();
  const { isMobile, isTablet } = useBreakpoint();

  const [profile, setProfile] = useState(null);
  const [preferences, setPreferences] = useState({
    notify_booking_updates: true, notify_job_status: true, notify_messages: true,
    notify_payments: true, notify_promotions: false, notify_product_updates: false,
  });
  const [addresses, setAddresses] = useState([]);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [activeSection, setActiveSection] = useState("profile");
  const [openMobileSection, setOpenMobileSection] = useState(null);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = "success") => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), type === "error" ? 4500 : 2600);
  }, []);
  const dismissToast = id => setToasts(prev => prev.filter(t => t.id !== id));

  const profileRef   = useRef(null);
  const securityRef  = useRef(null);
  const notifRef     = useRef(null);
  const paymentRef   = useRef(null);
  const addressesRef = useRef(null);
  const refsById = { profile: profileRef, security: securityRef, notifications: notifRef, payment: paymentRef, addresses: addressesRef };

    useEffect(() => {
    if (!isLoggedIn) { openLogin({ redirect: '/account' }); return; }
    (async () => {
      const { data: prof } = await supabase.from('profiles').select('*').eq('id', user.id).maybeSingle();
      setProfile(prof);
      const { data: prefsRow } = await supabase.from('notification_preferences').select('*').eq('user_id', user.id).maybeSingle();
      if (prefsRow) setPreferences(prefsRow);
      const { data: addrRows } = await supabase.from('saved_addresses').select('*').eq('user_id', user.id).order('is_default', { ascending: false });
      if (addrRows) setAddresses(addrRows);
      try {
        const { data: factorsData } = await supabase.auth.mfa.listFactors();
        const verifiedTotp = factorsData?.totp?.find(f => f.status === 'verified');
        if (verifiedTotp) { setMfaEnabled(true); setMfaFactorId(verifiedTotp.id); }
      } catch { /* MFA not configured on this project — toggle just stays off */ }
    })();
  }, [isLoggedIn, navigate, user]);

  // Scroll-spy — desktop only, since mobile has no scrolling list of sections.
  useEffect(() => {
    if (isMobile) return;
    const refs = [profileRef, securityRef, notifRef, paymentRef, addressesRef];
    const observer = new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (entry.isIntersecting) { const id = entry.target.getAttribute('data-section-id'); if (id) setActiveSection(id); }
      }
    }, { rootMargin: "-70px 0px -40% 0px", threshold: 0.25 });
    refs.forEach(r => { if (r.current) observer.observe(r.current); });
    return () => observer.disconnect();
  }, [isMobile]);

  const scrollToSection = id => {
    const el = refsById[id]?.current;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(id);
  };

  // Used by the profile-menu dropdown: on desktop it scrolls to the card;
  // on mobile it opens the matching accordion row instead.
  const jumpToSection = id => { if (isMobile) { setOpenMobileSection(id); } else { scrollToSection(id); } };

  /* Handlers — unchanged behaviour: every write is try/caught and
     surfaced via toast. */
  const handleUpdateProfile = async updates => {
    try {
      const { email, ...profileFields } = updates;
      const { error } = await supabase.from('profiles').update(profileFields).eq('id', user.id);
      if (error) throw error;
      if (email !== undefined && email !== profile?.email && email.trim()) {
        const { error: emailErr } = await supabase.auth.updateUser({ email: email.trim() });
        if (emailErr) throw emailErr;
        showToast("Profile saved. Check your new email to confirm the change.");
      } else {
        showToast("Profile saved.");
      }
      setProfile(prev => ({ ...prev, ...profileFields, ...(email !== undefined ? { email } : {}) }));
    } catch (err) {
      console.error("Profile update failed", err);
      showToast(err?.message || "Couldn't save your profile. Try again.", "error");
    }
  };
  const handleAvatarUpload = async file => {
    setUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `avatars/${user.id}/${Date.now()}.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('avatars').upload(path, file, { cacheControl: '3600', upsert: false });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
      const { error: updErr } = await supabase.from('profiles').update({ avatar_url: urlData.publicUrl }).eq('id', user.id);
      if (updErr) throw updErr;
      setProfile(prev => ({ ...prev, avatar_url: urlData.publicUrl }));
      showToast("Photo updated.");
    } catch (err) {
      console.error("Avatar upload failed", err);
      showToast(err?.message || "Couldn't upload that photo. Try again.", "error");
    } finally { setUploadingAvatar(false); }
  };
  const handleAvatarRemove = async () => {
    try {
      const { error } = await supabase.from('profiles').update({ avatar_url: null }).eq('id', user.id);
      if (error) throw error;
      setProfile(prev => ({ ...prev, avatar_url: null }));
      showToast("Photo removed.");
    } catch (err) { showToast(err?.message || "Couldn't remove photo.", "error"); }
  };
  const handlePasswordUpdate = async (currentPassword, newPassword) => {
    const { error: reauthErr } = await supabase.auth.signInWithPassword({ email: profile?.email || user.email, password: currentPassword });
    if (reauthErr) { showToast("Current password is incorrect.", "error"); throw reauthErr; }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) { showToast(error.message || "Couldn't update password.", "error"); throw error; }
    showToast("Password updated.");
  };
  const handleSignOutEverywhere = async () => {
    setSignOutConfirmOpen(false);
    try { await supabase.auth.signOut({ scope: 'global' }); } finally { logoutUser(); navigate('/login'); }
  };
  const handleUpdateMpesa = async phoneNumber => {
    try {
      const { error } = await supabase.from('profiles').update({ mpesa_phone: phoneNumber }).eq('id', user.id);
      if (error) throw error;
      setProfile(prev => ({ ...prev, mpesa_phone: phoneNumber }));
      showToast("M-Pesa number saved.");
    } catch (err) { showToast(err?.message || "Couldn't save that number.", "error"); }
  };
  const handleUpdatePrefs = async newPrefs => {
    const prevPrefs = preferences;
    setPreferences(newPrefs);
    try {
      const { error } = await supabase.from('notification_preferences').upsert({ user_id: user.id, ...newPrefs }, { onConflict: 'user_id' });
      if (error) throw error;
    } catch (err) { setPreferences(prevPrefs); showToast(err?.message || "Couldn't save that preference.", "error"); }
  };
  const handleAddAddress = async addr => {
    try {
      let newAddr = { ...addr, user_id: user.id };
      delete newAddr.id;
      if (newAddr.is_default) await supabase.from('saved_addresses').update({ is_default: false }).eq('user_id', user.id);
      const { data, error } = await supabase.from('saved_addresses').insert(newAddr).select().maybeSingle();
      if (error) throw error;
      setAddresses(prev => [data, ...prev]);
      showToast("Address added.");
    } catch (err) { showToast(err?.message || "Couldn't add that address.", "error"); }
  };
  const handleUpdateAddress = async addr => {
    try {
      if (addr.is_default) await supabase.from('saved_addresses').update({ is_default: false }).eq('user_id', user.id);
      const { error } = await supabase.from('saved_addresses').update(addr).eq('id', addr.id).eq('user_id', user.id);
      if (error) throw error;
      setAddresses(prev => prev.map(a => a.id === addr.id ? { ...a, ...addr } : a));
      showToast("Address updated.");
    } catch (err) { showToast(err?.message || "Couldn't update that address.", "error"); }
  };
  const handleDeleteAddress = async id => {
    try {
      const { error } = await supabase.from('saved_addresses').delete().eq('id', id).eq('user_id', user.id);
      if (error) throw error;
      setAddresses(prev => prev.filter(a => a.id !== id));
      showToast("Address deleted.");
    } catch (err) { showToast(err?.message || "Couldn't delete that address.", "error"); }
  };

  const initials = (profile?.full_name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  if (!isLoggedIn) return null;

  const sectionProps = {
    profile:       <ProfileFields profile={profile} onUpdateProfile={handleUpdateProfile} onAvatarUpload={handleAvatarUpload} onAvatarRemove={handleAvatarRemove} uploadingAvatar={uploadingAvatar} />,
    security:      <SecurityFields onPasswordUpdate={handlePasswordUpdate} mfaEnabled={mfaEnabled} mfaFactorId={mfaFactorId} onMfaChange={(enabled, factorId) => { setMfaEnabled(enabled); setMfaFactorId(factorId); }} onRequestSignOutDialog={() => setSignOutConfirmOpen(true)} showToast={showToast} />,
    notifications: <NotificationsFields prefs={preferences} onUpdatePrefs={handleUpdatePrefs} />,
    payment:       <PaymentRefundFields mpesaPhone={profile?.mpesa_phone || ""} onUpdateMpesa={handleUpdateMpesa} />,
    addresses:     <AddressesFields addresses={addresses} onAddAddress={handleAddAddress} onUpdateAddress={handleUpdateAddress} onDeleteAddress={handleDeleteAddress} />,
  };
  const sectionMeta = {
    profile: { title: "Profile", description: "Your name, photo, and contact details." },
    security: { title: "Security", description: "Password, two-factor authentication, and active sessions." },
    notifications: { title: "Notifications", description: "Choose how and when JiraniApp keeps you informed. Changes save automatically." },
    payment: { title: "Payment & refunds", description: "Where we send your money when a refund is issued." },
    addresses: { title: "Saved addresses", description: "Your go-to locations, ready when you book." },
  };

  if (!profile) {
    return <div style={{ minHeight: "100vh", background: isMobile ? T.white : T.bg }} />;
  }

  return (
    <div style={{ background: isMobile ? T.white : T.bg, minHeight: "100vh", fontFamily: T.sans, display: "flex" }}>
      {!isMobile && (
        <DesktopSidebar profile={profile} activeSection={activeSection} onNavigate={scrollToSection} onSignOut={() => setSignOutConfirmOpen(true)} />
      )}

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <TopBar profile={profile} initials={initials} navigate={navigate} user={user} onSignOutRequest={() => setSignOutConfirmOpen(true)} onJumpToSection={jumpToSection} search={search} setSearch={setSearch} />

        <main style={{ maxWidth: 1180, width: "100%", margin: "0 auto", padding: isMobile ? "20px 16px 48px" : "28px 32px 60px", flex: 1 }}>

          {/* Hero — desktop only; mobile goes straight to the account list */}
          {!isMobile && (
            <div style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{ width: 28, height: 1.5, background: T.green }} />
                <span style={{ fontSize: 10.5, fontWeight: 700, color: T.greenDark, fontFamily: T.sans, textTransform: "uppercase", letterSpacing: "0.12em" }}>Your account</span>
              </div>
              <h1 style={{ fontFamily: T.serif, fontWeight: 400, fontStyle: "italic", margin: 0, fontSize: "clamp(28px, 3vw, 38px)", lineHeight: 1.08, letterSpacing: "-0.02em", color: T.text }}>
                Good to have you here, {profile?.full_name?.split(" ")[0] || "neighbour"}.
              </h1>
            </div>
          )}

          <div style={{ display: "flex", gap: isMobile ? 0 : 28, flexDirection: isMobile ? "column" : "row", alignItems: "flex-start" }}>

            <div style={{ flex: 1, minWidth: 0, width: "100%" }}>
              {isMobile ? (
                /* True accordion — nothing renders until tapped. */
                <div>
                  {SECTIONS_CONFIG.map(s => (
                    <AccordionSection key={s.id} id={s.id} icon={s.icon} label={s.label} open={openMobileSection === s.id} onToggle={() => setOpenMobileSection(o => o === s.id ? null : s.id)}>
                      {sectionProps[s.id]}
                    </AccordionSection>
                  ))}

                  <div style={{ background: T.white, border: `1.5px solid ${T.border}`, borderRadius: T.r16, overflow: "hidden", boxShadow: T.s1, marginTop: 4 }}>
                    <button onClick={() => setSignOutConfirmOpen(true)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "13px 16px", background: "none", border: "none", cursor: "pointer", textAlign: "left", minHeight: 48 }}>
                      <span style={{ width: 32, height: 32, borderRadius: T.r10, flexShrink: 0, background: T.redTint, border: `1px solid ${T.redBd}`, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="logout" size={15} strokeWidth={1.8} stroke={T.red} /></span>
                      <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: T.red, fontFamily: T.sans }}>Sign out</span>
                    </button>
                  </div>
                </div>
              ) : (
                Object.entries(sectionMeta).map(([id, meta]) => (
                  <div key={id} ref={refsById[id]}>
                    <SectionCard id={id} icon={SECTIONS_CONFIG.find(s => s.id === id).icon} title={meta.title} description={meta.description}>
                      {sectionProps[id]}
                    </SectionCard>
                  </div>
                ))
              )}
            </div>

            {!isMobile && !isTablet && <RightPanel navigate={navigate} />}
          </div>

          <ProductCardsSection user={user} navigate={navigate} />

          <CustomerFooter navigate={navigate} />
        </main>
      </div>

      <ConfirmDialog open={signOutConfirmOpen} title="Sign out of all devices?" message="This will end every active session, including this one. You'll need to sign in again on all your devices." confirmLabel="Sign out everywhere" danger onConfirm={handleSignOutEverywhere} onCancel={() => setSignOutConfirmOpen(false)} />

      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}